document.documentElement.classList.remove("no-js");
document.documentElement.classList.add("js");

const SEARCH_TRACKING_PARAMS = ["_pos", "_psq", "_psid", "_ss", "_v"];

const stripSearchTrackingParams = () => {
  const url = new URL(window.location.href);
  let changed = false;

  SEARCH_TRACKING_PARAMS.forEach((key) => {
    if (!url.searchParams.has(key)) return;
    url.searchParams.delete(key);
    changed = true;
  });

  if (!changed) return;

  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
};

stripSearchTrackingParams();

document.addEventListener("change", (event) => {
  const selector = event.target.closest("[data-product-option-selector]");

  if (!selector) return;

  const form = selector.closest('form[action*="/cart/add"]');
  const idInput = form && form.querySelector('input[name="id"]');
  const selectedOption = selector.options[selector.selectedIndex];

  if (idInput && selectedOption && selectedOption.dataset.variantId) {
    idInput.value = selectedOption.dataset.variantId;
  }
});

const isReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// iOS can still scroll the page behind an overflow-hidden mobile overlay.
// Freeze the page at its current position while keeping the overlay scrollable.
const mobileModalViewport = window.matchMedia("(max-width: 1199px)");
const mobileModalBodyClasses = [
  "athoor-search-open",
  "athoor-menu-open",
  "athoor-filter-modal-open",
  "athoor-event-project-modal-open",
  "athoor-product-buy-modal-open",
];
let mobileModalPageLock = null;

const syncMobileModalPageLock = () => {
  const body = document.body;
  const shouldLock =
    mobileModalViewport.matches && mobileModalBodyClasses.some((name) => body.classList.contains(name));

  if (shouldLock && !mobileModalPageLock) {
    mobileModalPageLock = {
      x: window.scrollX,
      y: window.scrollY,
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
    };
    body.style.position = "fixed";
    body.style.top = `${-mobileModalPageLock.y}px`;
    body.style.left = `${-mobileModalPageLock.x}px`;
    body.style.right = "0";
    body.style.width = "100%";
    updateHeaderScrollState(mobileModalPageLock.y);
    return;
  }

  if (!shouldLock && mobileModalPageLock) {
    const saved = mobileModalPageLock;
    mobileModalPageLock = null;
    body.style.position = saved.position;
    body.style.top = saved.top;
    body.style.left = saved.left;
    body.style.right = saved.right;
    body.style.width = saved.width;

    const root = document.documentElement;
    const scrollBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = "auto";
    window.scrollTo(saved.x, saved.y);
    root.style.scrollBehavior = scrollBehavior;
    updateHeaderScrollState(saved.y);
    requestAnimationFrame(updateHeaderScrollState);
  }
};

new MutationObserver(syncMobileModalPageLock).observe(document.body, {
  attributes: true,
  attributeFilter: ["class"],
});
mobileModalViewport.addEventListener?.("change", syncMobileModalPageLock);
syncMobileModalPageLock();

const getSliderStep = (track) => {
  const card = track.querySelector(":scope > *");
  const styles = getComputedStyle(track);
  const gap = parseFloat(styles.columnGap || styles.gap) || 0;

  return (card ? card.getBoundingClientRect().width : track.clientWidth) + gap;
};

const getSliderControls = (track) =>
  Array.from(document.querySelectorAll("[data-scroll-target]")).filter(
    (control) => control.dataset.scrollTarget === track.id,
  );

const updateSliderControls = (track) => {
  if (track.classList.contains("is-crawling")) {
    getSliderControls(track).forEach((control) => {
      control.disabled = false;
      control.setAttribute("aria-disabled", "false");
    });
    return;
  }

  const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
  const atStart = track.scrollLeft <= 1;
  const atEnd = track.scrollLeft >= maxScroll - 1;

  getSliderControls(track).forEach((control) => {
    const shouldDisable =
      (control.dataset.scrollDirection === "previous" && atStart) ||
      (control.dataset.scrollDirection === "next" && (atEnd || maxScroll <= 1));

    control.disabled = shouldDisable;
    control.setAttribute("aria-disabled", String(shouldDisable));
  });
};

const scrollSliderToIndex = (track, index, behavior) => {
  const step = getSliderStep(track);
  const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
  const maxIndex = step ? Math.ceil(maxScroll / step) : 0;
  const nextIndex = Math.min(Math.max(index, 0), maxIndex);

  track.scrollTo({
    left: Math.min(nextIndex * step, maxScroll),
    behavior: isReducedMotion() ? "auto" : behavior,
  });
};

const snapSliderToNearest = (track, behavior = "smooth") => {
  const step = getSliderStep(track);

  if (!step) return;

  scrollSliderToIndex(track, Math.round(track.scrollLeft / step), behavior);
};

const scrollSliderByDirection = (control) => {
  const track = document.getElementById(control.dataset.scrollTarget);

  if (!track) return;

  const direction = control.dataset.scrollDirection === "previous" ? -1 : 1;
  const currentIndex = Math.round(track.scrollLeft / getSliderStep(track));

  scrollSliderToIndex(track, currentIndex + direction, "smooth");
};

const initSliderTrack = (track) => {
  if (track.dataset.sliderReady === "true") return;

  let startX = 0;
  let startScroll = 0;
  let moved = false;

  track.dataset.sliderReady = "true";
  updateSliderControls(track);

  track.addEventListener(
    "scroll",
    () => {
      updateSliderControls(track);
    },
    { passive: true },
  );

  track.addEventListener("pointerdown", (event) => {
    if (event.button !== undefined && event.button !== 0) return;

    startX = event.clientX;
    startScroll = track.scrollLeft;
    moved = false;
    track.dataset.dragging = "true";
    track.setPointerCapture?.(event.pointerId);
  });

  track.addEventListener(
    "pointermove",
    (event) => {
      if (track.dataset.dragging !== "true") return;

      const deltaX = event.clientX - startX;
      moved = moved || Math.abs(deltaX) > 3;
      track.scrollLeft = startScroll - deltaX;

      if (moved) event.preventDefault();
    },
    { passive: false },
  );

  const endDrag = (event) => {
    if (track.dataset.dragging !== "true") return;

    delete track.dataset.dragging;
    track.releasePointerCapture?.(event.pointerId);
    snapSliderToNearest(track);
  };

  track.addEventListener("pointerup", endDrag);
  track.addEventListener("pointercancel", endDrag);
};

const initSliderTracks = () => {
  document.querySelectorAll("[data-slider-track]").forEach(initSliderTrack);
};

const initDragScroll = (track) => {
  if (track.dataset.dragScrollReady === "true") return;

  let startX = 0;
  let startScroll = 0;
  let moved = false;

  track.dataset.dragScrollReady = "true";

  track.addEventListener("pointerdown", (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    if (Number(track.dataset.dragScrollMinWidth || 0) > window.innerWidth) return;

    startX = event.clientX;
    startScroll = track.scrollLeft;
    moved = false;
    track.dataset.dragging = "true";
    track.setPointerCapture?.(event.pointerId);
  });

  track.addEventListener("pointermove", (event) => {
    if (track.dataset.dragging !== "true") return;

    const deltaX = event.clientX - startX;
    moved = moved || Math.abs(deltaX) > 3;
    track.scrollLeft = startScroll - deltaX;

    if (moved) event.preventDefault();
  });

  const endDrag = (event) => {
    if (track.dataset.dragging !== "true") return;

    delete track.dataset.dragging;
    track.releasePointerCapture?.(event.pointerId);
  };

  track.addEventListener("pointerup", endDrag);
  track.addEventListener("pointercancel", endDrag);
};

const initDragScrollTracks = () => {
  document.querySelectorAll("[data-drag-scroll]").forEach(initDragScroll);
};

const getDocumentTop = (element) => {
  let top = 0;
  let node = element;

  while (node) {
    top += node.offsetTop;
    node = node.offsetParent;
  }

  return top;
};

const STORY_SCROLL_TRAVEL_RATIO = 0.76;

const getStoryTrackTravel = (pin, track) => {
  const firstSlide = track.querySelector(".athoor-story-slider__slide");
  const lastSlide = track.querySelector(".athoor-story-slider__slide:last-of-type");

  if (!firstSlide || !lastSlide) return 0;

  return Math.max(lastSlide.offsetLeft - firstSlide.offsetLeft, 0);
};

const clearStoryStageLock = (stage, slot) => {
  if (stage) {
    stage.style.removeProperty("position");
    stage.style.removeProperty("top");
    stage.style.removeProperty("bottom");
    stage.style.removeProperty("left");
    stage.style.removeProperty("width");
  }

  if (slot) slot.style.removeProperty("height");
};

const updateHorizontalPins = () => {
  document.querySelectorAll("[data-horizontal-pin]").forEach((section) => {
    const stage = section.querySelector(".athoor-story-slider__stage");
    const slot = section.querySelector(".athoor-story-slider__slot");
    const pin = section.querySelector(".athoor-story-slider__pin");
    const track = section.querySelector("[data-horizontal-pin-track]");

    if (!stage || !pin || !track) return;

    if (isReducedMotion() || window.innerWidth < 641) {
      delete section.dataset.storyTravel;
      delete section.dataset.storyTravelKey;
      track.style.transform = "";
      clearStoryStageLock(stage, slot);
      return;
    }

    const measureKey = String(pin.clientWidth);
    let maxTranslate = Number.parseFloat(section.dataset.storyTravel);

    if (section.dataset.storyTravelKey !== measureKey || Number.isNaN(maxTranslate) || maxTranslate <= 0) {
      maxTranslate = getStoryTrackTravel(pin, track);
      if (maxTranslate > 0) {
        section.dataset.storyTravel = String(maxTranslate);
        section.dataset.storyTravelKey = measureKey;
      }
    }

    if (!maxTranslate) {
      track.style.transform = "translate3d(0, 0, 0)";
      clearStoryStageLock(stage, slot);
      return;
    }

    const header = document.querySelector(".athoor-header");
    const stickyTop = header ? Math.round(header.getBoundingClientRect().height) : 58;
    const stageHeight = stage.offsetHeight;
    const scrollTravel = maxTranslate * STORY_SCROLL_TRAVEL_RATIO;

    if (slot) slot.style.height = `${stageHeight + scrollTravel}px`;

    const startY = getDocumentTop(slot || stage) - stickyTop;
    const progress = Math.min(Math.max((window.scrollY - startY) / scrollTravel, 0), 1);

    track.style.transform = `translate3d(${-maxTranslate * progress}px, 0, 0)`;

    if (window.scrollY < startY) {
      stage.style.removeProperty("position");
      stage.style.removeProperty("top");
      stage.style.removeProperty("bottom");
      stage.style.removeProperty("left");
      stage.style.removeProperty("width");
      return;
    }

    if (window.scrollY >= startY + scrollTravel) {
      stage.style.position = "absolute";
      stage.style.top = "auto";
      stage.style.bottom = "0";
      stage.style.left = "0";
      stage.style.width = "100%";
      return;
    }

    stage.style.position = "fixed";
    stage.style.top = `${stickyTop}px`;
    stage.style.bottom = "auto";
    stage.style.left = `${section.getBoundingClientRect().left}px`;
    stage.style.width = `${section.clientWidth}px`;
  });
};

let horizontalPinFrame = null;

const scheduleHorizontalPinUpdate = () => {
  if (horizontalPinFrame) return;

  horizontalPinFrame = window.requestAnimationFrame(() => {
    horizontalPinFrame = null;
    updateHorizontalPins();
  });
};

const supportsScrollTimeline = () => typeof CSS !== "undefined" && CSS.supports?.("animation-timeline: scroll()");

const getCollectionGradientPage = () => {
  if (!document.body.matches(".template-collection:has(.athoor-collection--products)")) return null;

  return document.body;
};

const updateCollectionGradient = () => {
  const page = getCollectionGradientPage();

  if (!page || supportsScrollTimeline()) return;

  if (isReducedMotion()) {
    page.style.removeProperty("--athoor-gradient-shift");
    return;
  }

  const maxScroll = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
  const progress = Math.min(Math.max(window.scrollY / maxScroll, 0), 1);

  page.style.setProperty("--athoor-gradient-shift", `${(progress * 100).toFixed(2)}%`);
};

let collectionGradientFrame = null;

const scheduleCollectionGradientUpdate = () => {
  if (collectionGradientFrame || supportsScrollTimeline()) return;

  collectionGradientFrame = window.requestAnimationFrame(() => {
    collectionGradientFrame = null;
    updateCollectionGradient();
  });
};

let productCardRevealObserver = null;

const revealProductCard = (card) => {
  card.classList.add("is-in-view");
  productCardRevealObserver?.unobserve(card);
};

let byTheNumbersObserver;
const STAT_STAGGER_MS = 90;

const parseStatValue = (raw) => {
  const match = String(raw)
    .trim()
    .match(/^([\d,]+)(.*)$/);

  if (!match) return null;

  const amount = Number(match[1].replace(/,/g, ""));

  if (!Number.isFinite(amount)) return null;

  return {
    amount,
    suffix: match[2],
    useComma: match[1].includes(","),
  };
};

const formatStatAmount = (amount, useComma) => (useComma ? amount.toLocaleString("en-US") : String(amount));

const setStatValueDisplay = (el, text) => {
  const match = String(text).match(/^(.*?)(\s+)(sq\.?\s*ft\.?)\s*$/i);

  if (!match) {
    el.textContent = text;
    return;
  }

  const unit = document.createElement("span");
  unit.className = "athoor-by-the-numbers__unit";
  unit.textContent = match[3];
  el.replaceChildren(match[1], unit);
};

const animateStatValue = (el) => {
  const raw = el.dataset.statValue || "";
  const parsed = parseStatValue(raw);

  if (!parsed || isReducedMotion()) {
    setStatValueDisplay(el, raw);
    return;
  }

  const duration = 900;
  const start = performance.now();

  const tick = (now) => {
    if (!el.isConnected) return;

    const t = Math.min((now - start) / duration, 1);
    const eased = 1 - (1 - t) ** 3;
    const current = Math.round(parsed.amount * eased);

    setStatValueDisplay(el, `${formatStatAmount(current, parsed.useComma)}${parsed.suffix}`);
    if (t < 1) requestAnimationFrame(tick);
  };

  setStatValueDisplay(el, `${formatStatAmount(0, parsed.useComma)}${parsed.suffix}`);
  requestAnimationFrame(tick);
};

const initByTheNumbersSlider = (section) => {
  if (!section || section.dataset.numbersSliderReady === "true") return;

  const media = section.querySelector("[data-numbers-media]");
  const slides = Array.from(section.querySelectorAll("[data-numbers-slide]"));
  const pauseButton = section.querySelector("[data-numbers-pause]");
  const holdMs = 5000;

  section.dataset.numbersSliderReady = "true";

  if (!media || slides.length < 2) return;

  let index = Math.max(
    slides.findIndex((slide) => slide.classList.contains("is-active")),
    0,
  );
  let timer = null;
  let userPaused = false;

  const shouldAutoplay = () => !isReducedMotion() && !userPaused;

  const setActive = (next) => {
    const total = slides.length;
    const target = ((next % total) + total) % total;

    slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === target;

      slide.classList.toggle("is-active", isActive);
      slide.setAttribute("aria-hidden", String(!isActive));
    });

    index = target;
  };

  const stopAutoplay = () => {
    window.clearTimeout(timer);
    timer = null;
  };

  const armAutoplay = () => {
    stopAutoplay();
    if (!shouldAutoplay()) return;

    timer = window.setTimeout(() => {
      setActive(index + 1);
      armAutoplay();
    }, holdMs);
  };

  const syncPauseButton = () => {
    if (!pauseButton) return;

    const label = pauseButton.querySelector(".athoor-button__label");
    const playing = !userPaused;

    if (label) label.textContent = playing ? "Pause" : "Play";
    pauseButton.setAttribute("aria-pressed", String(userPaused));
    pauseButton.setAttribute("aria-label", playing ? "Pause image slideshow" : "Play image slideshow");
  };

  pauseButton?.addEventListener("click", () => {
    userPaused = !userPaused;
    syncPauseButton();

    if (userPaused) stopAutoplay();
    else armAutoplay();
  });

  syncPauseButton();
  setActive(index);
  armAutoplay();
};

const playByTheNumbers = (section) => {
  if (section.dataset.statsPlayed === "true") return;

  section.dataset.statsPlayed = "true";
  const rows = Array.from(section.querySelectorAll("[data-stat-row]"));

  rows.forEach((row, index) => {
    const delay = isReducedMotion() ? 0 : index * STAT_STAGGER_MS;
    const value = row.querySelector("[data-stat-value]");

    row.style.setProperty("--athoor-stat-delay", `${delay}ms`);
    row.classList.add("is-in-view");

    window.setTimeout(() => {
      if (value) animateStatValue(value);
    }, delay);
  });
};

const initByTheNumbers = (scope = document) => {
  const root = scope.querySelectorAll ? scope : document;
  const sections = [];

  if (root.matches?.("[data-by-the-numbers]")) sections.push(root);
  root.querySelectorAll?.("[data-by-the-numbers]").forEach((section) => sections.push(section));

  if (!sections.length) return;

  if (isReducedMotion()) {
    sections.forEach((section) => {
      initByTheNumbersSlider(section);
      playByTheNumbers(section);
    });
    return;
  }

  if (!byTheNumbersObserver) {
    byTheNumbersObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          playByTheNumbers(entry.target);
          byTheNumbersObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.28, rootMargin: "0px 0px -8% 0px" },
    );
  }

  sections.forEach((section) => {
    initByTheNumbersSlider(section);
    if (section.dataset.statsPlayed === "true") return;
    byTheNumbersObserver.observe(section);
  });
};

const initProductCardReveal = (scope = document) => {
  const root = scope.querySelectorAll ? scope : document;
  const cards = root.querySelectorAll(".product-card");

  if (!cards.length) return;

  if (isReducedMotion()) {
    cards.forEach((card) => {
      card.classList.add("is-in-view");
      delete card.dataset.cardReveal;
    });
    return;
  }

  if (!productCardRevealObserver) {
    productCardRevealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          revealProductCard(entry.target);
        });
      },
      { threshold: 0.14, rootMargin: "0px 0px -6% 0px" },
    );
  }

  cards.forEach((card) => {
    if (card.classList.contains("is-in-view")) return;
    card.dataset.cardReveal = "ready";
    productCardRevealObserver.observe(card);
  });
};

const PREMIUM_REVEAL_GROUPS = [
  {
    selector:
      ".athoor-editorial-hero__heading, .athoor-product-detail__title, .athoor-signature-intro__heading, .athoor-story-slider__heading, .athoor-events-page__title, .athoor-contact-page__heading, .athoor-page__title, .athoor-related-products__heading, .athoor-cart__heading, .athoor-cta__heading",
    kind: "heading",
    stagger: 0,
  },
  {
    selector:
      ".athoor-home-banner__text, .athoor-home-banner__features li, .athoor-editorial-hero__copy, .athoor-signature-intro__text, .athoor-partner-reveal__copy h2, .athoor-partner-reveal__text, .athoor-collection-overview__copy, .athoor-product-detail__sku, .athoor-product-detail__description, .athoor-product-detail__actions, .athoor-cart__copy",
    kind: "content",
    stagger: 70,
  },
  {
    selector:
      ".athoor-trust-intro__eyebrow, .athoor-trust-intro__heading, .athoor-by-the-numbers__eyebrow, .athoor-by-the-numbers__heading, .athoor-logo-marquee__heading, .athoor-testimonial-slider__heading, .athoor-faq__heading",
    kind: "heading",
    stagger: 60,
  },
  {
    selector:
      ".athoor-trust-intro__proof-card, .athoor-testimonial-slider__card, .athoor-event-project-card, .athoor-faq .athoor-faq__item, .athoor-cta__panel, .athoor-footer__catalog-button, .athoor-footer__menu",
    kind: "card",
    stagger: 70,
  },
  {
    selector:
      ".athoor-by-the-numbers__media, .athoor-by-the-numbers__text, .athoor-collection-overview__image, .athoor-footer__bottom",
    kind: "media",
    stagger: 80,
  },
  { selector: ".athoor-showreel__label", kind: "card", stagger: 0 },
  { selector: ".athoor-logo-marquee__viewport", kind: "media", stagger: 90 },
  { selector: ".athoor-story-slider__slide", kind: "card", stagger: 70, maxWidth: 640 },
];

let premiumRevealObserver;
let premiumRevealFrame = 0;
let premiumParallaxFrame = 0;

const getScopedElements = (scope, selector) => {
  const elements = Array.from(scope.querySelectorAll?.(selector) || []);

  if (scope.matches?.(selector)) elements.unshift(scope);

  return elements;
};

const revealPremiumElement = (element) => {
  element.classList.add("is-in-view");
  premiumRevealObserver?.unobserve(element);
};

const updatePremiumReveals = () => {
  premiumRevealFrame = 0;

  document.querySelectorAll('[data-athoor-reveal="ready"]:not(.is-in-view)').forEach((element) => {
    const bounds = element.getBoundingClientRect();

    if (bounds.bottom > 0 && bounds.top < window.innerHeight * 0.9) revealPremiumElement(element);
  });
};

const schedulePremiumRevealUpdate = () => {
  if (premiumRevealFrame) return;
  premiumRevealFrame = window.requestAnimationFrame(updatePremiumReveals);
};

const initPremiumScrollReveals = (scope = document) => {
  if (isReducedMotion()) {
    PREMIUM_REVEAL_GROUPS.forEach(({ selector }) => {
      getScopedElements(scope, selector).forEach((element) => element.classList.add("is-in-view"));
    });
    return;
  }

  if (!premiumRevealObserver) {
    premiumRevealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) revealPremiumElement(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -10% 0px" },
    );
  }

  PREMIUM_REVEAL_GROUPS.forEach(({ selector, kind, stagger, maxWidth }) => {
    if (maxWidth && window.innerWidth > maxWidth) return;

    getScopedElements(scope, selector).forEach((element, index) => {
      if (element.dataset.athoorReveal === "ready" || element.classList.contains("is-in-view")) return;

      element.dataset.athoorReveal = "ready";
      element.dataset.athoorRevealKind = kind;
      element.style.setProperty("--athoor-reveal-delay", String(Math.min(index % 5, 4) * stagger) + "ms");

      const bounds = element.getBoundingClientRect();
      const startsInViewport = bounds.bottom > 0 && bounds.top < window.innerHeight;

      if (startsInViewport) {
        window.requestAnimationFrame(() => revealPremiumElement(element));
        return;
      }

      premiumRevealObserver.observe(element);
    });
  });

  schedulePremiumRevealUpdate();
};

const updatePremiumParallax = () => {
  premiumParallaxFrame = 0;

  document.querySelectorAll("[data-athoor-parallax]").forEach((section) => {
    if (isReducedMotion()) {
      section.style.setProperty("--athoor-parallax-y", "0px");
      return;
    }

    const bounds = section.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    if (bounds.bottom < -viewportHeight * 0.25 || bounds.top > viewportHeight * 1.25) return;

    const progress = (bounds.top + bounds.height / 2 - viewportHeight / 2) / (viewportHeight + bounds.height);
    const range = Math.min(bounds.height * 0.02, window.innerWidth <= 1024 ? 7 : 14);
    const offset = Math.max(-range, Math.min(range, progress * range * -2));

    section.style.setProperty("--athoor-parallax-y", offset.toFixed(2) + "px");
  });
};

const schedulePremiumParallaxUpdate = () => {
  if (premiumParallaxFrame) return;
  premiumParallaxFrame = window.requestAnimationFrame(updatePremiumParallax);
};

const initPremiumParallax = (scope = document) => {
  [".athoor-showreel"].forEach((selector) => {
    getScopedElements(scope, selector).forEach((section) => {
      section.dataset.athoorParallax = "true";
    });
  });
  schedulePremiumParallaxUpdate();
};

const initTrustIntroHover = (stage) => {
  if (stage.dataset.trustIntroReady === "true") return;

  stage.dataset.trustIntroReady = "true";

  stage.querySelectorAll(".athoor-trust-intro__proof-card").forEach((card) => {
    const match = card.className.match(/athoor-trust-intro__proof-card--(\d)/);
    const media = card.querySelector(".athoor-trust-intro__proof-media");

    if (!match || !media) return;

    media.addEventListener("pointerenter", () => {
      stage.dataset.activeProof = match[1];
    });
    media.addEventListener("focus", () => {
      stage.dataset.activeProof = match[1];
    });
  });

  stage.addEventListener("pointerleave", () => {
    delete stage.dataset.activeProof;
  });
  stage.addEventListener("focusout", (event) => {
    if (!stage.contains(event.relatedTarget)) delete stage.dataset.activeProof;
  });
};

const initTrustIntroHovers = () => {
  document.querySelectorAll(".athoor-trust-intro__stage").forEach(initTrustIntroHover);
};

const initLoopingVideos = (root = document) => {
  const media = root.matches?.("video")
    ? [root]
    : Array.from(root.querySelectorAll("[data-event-card] video, [data-event-gallery-autoplay] video"));

  media.forEach((video) => {
    if (video.dataset.loopingReady === "true") return;

    video.dataset.loopingReady = "true";
    video.muted = true;
    video.playsInline = true;

    const sync = (inView) => {
      const pausedByUser = video.closest("[data-video-paused='true']");

      if (pausedByUser || isReducedMotion() || !inView) {
        video.pause();
        return;
      }

      video.play()?.catch(() => {});
    };

    if (!("IntersectionObserver" in window)) {
      sync(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => sync(entry.isIntersecting && entry.intersectionRatio >= 0.25));
      },
      { threshold: 0.25 },
    );

    observer.observe(video);
  });
};

const showreelAutoplays = (section) => section.dataset.showreelAutoplay === "true";

const setShowreelButton = (section, playing) => {
  const play = section.querySelector("[data-showreel-play]");

  if (!play) return;

  play.textContent = playing ? play.dataset.pauseLabel || "Pause" : play.dataset.playLabel || "Showreel";
  play.setAttribute("aria-pressed", playing ? "true" : "false");
};

const setShowreelPlaying = (section, playing) => {
  const video = section.querySelector("video");

  if (!video) return;

  video.playsInline = true;

  if (playing) {
    const keepMuted = showreelAutoplays(section) && !isReducedMotion();

    delete section.dataset.showreelUserPaused;

    if (keepMuted) {
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute("muted", "");
    } else {
      video.muted = false;
      video.defaultMuted = false;
      video.removeAttribute("muted");
      video.volume = 1;
    }

    video.removeAttribute("poster");
    section.classList.add("is-playing", "is-started");
    video.play()?.catch(() => {});
    setShowreelButton(section, true);
    return;
  }

  section.dataset.showreelUserPaused = "true";
  section.classList.remove("is-playing");
  video.pause();
  setShowreelButton(section, false);
};

const initShowreel = (root = document) => {
  const sections = root.matches?.("[data-showreel]") ? [root] : Array.from(root.querySelectorAll("[data-showreel]"));

  sections.forEach((section) => {
    if (section.dataset.showreelReady === "true") return;

    const video = section.querySelector("video");

    if (!video) return;

    section.dataset.showreelReady = "true";
    const startedWithSound = section.classList.contains("is-playing") && !video.muted;
    video.muted = true;
    video.playsInline = true;

    if (startedWithSound) {
      video.muted = false;
      video.defaultMuted = false;
      video.removeAttribute("muted");
      video.volume = 1;
    } else if (showreelAutoplays(section) && !isReducedMotion() && section.dataset.showreelUserPaused !== "true") {
      video.defaultMuted = true;
      video.setAttribute("muted", "");
      section.classList.add("is-playing", "is-started");
      setShowreelButton(section, true);
    } else if (showreelAutoplays(section)) {
      video.pause();
      video.autoplay = false;
    }

    const sync = (inView) => {
      if (!section.classList.contains("is-playing") || !inView) {
        video.pause();
        return;
      }

      video.play()?.catch(() => {});
    };

    if (!("IntersectionObserver" in window)) {
      sync(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => sync(entry.isIntersecting && entry.intersectionRatio >= 0.25));
      },
      { threshold: 0.25 },
    );

    observer.observe(video);
  });
};

document.addEventListener("click", (event) => {
  const play = event.target.closest("[data-showreel-play]");
  const section = event.target.closest("[data-showreel]");

  if (play instanceof HTMLElement) {
    const host = play.closest("[data-showreel]");

    if (!host) return;

    const pendingAutoplay = showreelAutoplays(host) && !isReducedMotion() && host.dataset.showreelReady !== "true";

    setShowreelPlaying(host, !(host.classList.contains("is-playing") || pendingAutoplay));
    return;
  }

  if (
    section instanceof HTMLElement &&
    section.classList.contains("is-playing") &&
    event.target.closest(".athoor-showreel__media")
  ) {
    setShowreelPlaying(section, false);
  }
});

const EVENT_PROJECT_FOCUSABLE = 'a[href], button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

let eventProjectLastFocus = null;

const getEventProjectPanel = (root) => root?.querySelector("[data-event-project-panel]");

const getOpenEventProjectGallery = () => document.querySelector('[data-event-gallery][data-project-open="true"]');

const getEventProjectFocusable = (panel) =>
  [...(panel?.querySelectorAll(EVENT_PROJECT_FOCUSABLE) || [])].filter((element) => !element.closest("[inert]"));

const setEventProjectPageInert = (root, open) => {
  document.querySelector("[data-header]")?.toggleAttribute("inert", open);
  document.querySelector(".shopify-section-group-footer-group")?.toggleAttribute("inert", open);
  document.querySelector(".athoor-floating-contact")?.toggleAttribute("inert", open);

  const projectSection = root.closest(".shopify-section") || root;

  document.querySelectorAll("#MainContent > *").forEach((child) => {
    if (child === projectSection) return;
    child.toggleAttribute("inert", open);
  });

  [...root.children].forEach((child) => {
    if (child.classList.contains("athoor-modal--event-project")) return;
    child.toggleAttribute("inert", open);
  });
};

const getEventProjectFrame = (root) => root?.querySelector("[data-event-project-frame]");

const normalizeEventProjectGalleryUrl = (value) => {
  try {
    const url = new URL(value);

    return ["http:", "https:"].includes(url.protocol) ? url.href : "";
  } catch {
    return "";
  }
};

const unloadEventProjectFrame = (root) => {
  const frame = getEventProjectFrame(root);

  if (!frame) return;

  frame.src = "about:blank";
  frame.removeAttribute("src");
  frame.title = "Event gallery";
  root.dataset.projectLoading = "false";
};

const syncEventProjectDialog = (root, open) => {
  const panel = getEventProjectPanel(root);
  const backdrop = root?.querySelector(".athoor-modal--event-project .athoor-modal__backdrop");

  panel?.toggleAttribute("inert", !open);
  panel?.setAttribute("aria-hidden", open ? "false" : "true");
  backdrop?.toggleAttribute("inert", !open);
};

const closeEventProjectGallery = (root, restoreFocus = true) => {
  if (!root) return;

  root.dataset.projectOpen = "false";
  syncEventProjectDialog(root, false);
  setEventProjectPageInert(root, false);
  document.body.classList.remove("athoor-event-project-modal-open");
  unloadEventProjectFrame(root);

  if (restoreFocus && eventProjectLastFocus?.isConnected) eventProjectLastFocus.focus();
  eventProjectLastFocus = null;
};

const openEventProjectGallery = (trigger) => {
  const root = trigger?.closest("[data-event-gallery]");
  const project = trigger?.closest("[data-event-project]");
  const galleryUrl = normalizeEventProjectGalleryUrl(project?.dataset.projectGalleryUrl);
  const frame = getEventProjectFrame(root);

  if (!root || !project || !frame || !galleryUrl) return;

  const openGallery = getOpenEventProjectGallery();

  if (openGallery && openGallery !== root) closeEventProjectGallery(openGallery, false);

  eventProjectLastFocus = trigger;

  const title = root.querySelector("[data-event-project-modal-title]");
  const projectTitle = project.dataset.projectTitle || "Event gallery";

  if (title) title.textContent = projectTitle;

  root.dataset.projectOpen = "true";
  root.dataset.projectLoading = "true";
  syncEventProjectDialog(root, true);
  setEventProjectPageInert(root, true);
  document.body.classList.add("athoor-event-project-modal-open");
  frame.title = projectTitle;
  frame.src = galleryUrl;

  window.requestAnimationFrame(() => {
    root.querySelector("[data-event-project-close]")?.focus();
  });
};

const sortEventProjectCards = (root) => {
  const grid = root?.querySelector(".athoor-event-gallery__grid");
  const cards = [...(grid?.querySelectorAll("[data-event-project]") || [])];

  if (!grid || !cards.length) return;

  const orderedCards = cards
    .map((card, sourceIndex) => ({
      card,
      sourceIndex,
      position: Number.parseFloat(card.dataset.projectPosition),
    }))
    .sort((a, b) => {
      const aPosition = Number.isFinite(a.position) ? a.position : Number.POSITIVE_INFINITY;
      const bPosition = Number.isFinite(b.position) ? b.position : Number.POSITIVE_INFINITY;

      return aPosition - bPosition || a.sourceIndex - b.sourceIndex;
    });

  grid.querySelectorAll(".athoor-event-gallery__row").forEach((row) => row.remove());

  let row = null;

  orderedCards.forEach(({ card }, index) => {
    const pattern = index % 10;
    const span = pattern === 1 || pattern === 8 ? 3 : pattern === 2 || pattern === 7 ? 2 : 1;
    let rowClasses = "";

    if (pattern < 2) rowClasses = "athoor-event-gallery__row--pair";
    else if (pattern < 5) rowClasses = "athoor-event-gallery__row--trio";
    else if (pattern < 8) {
      rowClasses = "athoor-event-gallery__row--trio athoor-event-gallery__row--trio-reverse";
    } else {
      rowClasses = "athoor-event-gallery__row--pair athoor-event-gallery__row--pair-reverse";
    }

    if (pattern === 0 || pattern === 2 || pattern === 5 || pattern === 8) {
      row = document.createElement("div");
      row.className = `athoor-event-gallery__row ${rowClasses}`;
      grid.append(row);
    }

    card.classList.remove(
      "athoor-event-project-card--span-1",
      "athoor-event-project-card--span-2",
      "athoor-event-project-card--span-3",
      "athoor-event-project-card--span-4",
      "athoor-event-project-card--tablet-wide",
    );
    card.classList.add(`athoor-event-project-card--span-${span}`);
    card.classList.toggle("athoor-event-project-card--tablet-wide", (index + 1) % 5 === 0);
    row?.append(card);
  });

  grid.dataset.eventGalleryGrouped = "true";

  grid.querySelectorAll(".athoor-event-gallery__row").forEach((galleryRow) => {
    let resizeLock = 0;

    galleryRow.addEventListener("pointerover", (event) => {
      const card = event.target.closest(".athoor-event-project-card");
      if (!card || !galleryRow.contains(card) || resizeLock) return;

      const index = [...galleryRow.children].indexOf(card) + 1;
      if (index < 1 || galleryRow.dataset.featuredIndex === String(index)) return;

      galleryRow.dataset.featuredIndex = String(index);
      resizeLock = window.setTimeout(() => {
        resizeLock = 0;
      }, 680);
    });

    galleryRow.addEventListener("pointerleave", () => {
      window.clearTimeout(resizeLock);
      resizeLock = 0;
      delete galleryRow.dataset.featuredIndex;
    });

    galleryRow.addEventListener("focusin", (event) => {
      const card = event.target.closest(".athoor-event-project-card");
      if (!card || !galleryRow.contains(card)) return;

      const index = [...galleryRow.children].indexOf(card) + 1;
      if (index > 0) galleryRow.dataset.featuredIndex = String(index);
    });

    galleryRow.addEventListener("focusout", () => {
      window.requestAnimationFrame(() => {
        if (!galleryRow.contains(document.activeElement)) delete galleryRow.dataset.featuredIndex;
      });
    });
  });
};

const initEventGallery = (scope = document) => {
  const roots = scope.matches?.("[data-event-gallery]")
    ? [scope]
    : [...(scope.querySelectorAll?.("[data-event-gallery]") || [])];

  roots.forEach((root) => {
    sortEventProjectCards(root);
    initLoopingVideos(root);

    if (root.dataset.eventGalleryReady === "true") return;

    root.dataset.eventGalleryReady = "true";
    root.dataset.projectOpen = "false";
    syncEventProjectDialog(root, false);

    const frame = getEventProjectFrame(root);

    frame?.addEventListener("load", () => {
      if (root.dataset.projectOpen === "true" && frame.hasAttribute("src")) {
        root.dataset.projectLoading = "false";
      }
    });
  });
};

document.addEventListener("click", (event) => {
  const card = event.target.closest("[data-event-project]");
  const openTrigger =
    event.target.closest("[data-event-project-open]") || card?.querySelector("[data-event-project-open]");

  if (openTrigger) {
    openEventProjectGallery(openTrigger);
    return;
  }

  const root = event.target.closest("[data-event-gallery]");

  if (!root) return;

  if (event.target.closest("[data-event-project-close]")) {
    closeEventProjectGallery(root);
    return;
  }
});

document.addEventListener("keydown", (event) => {
  const root = getOpenEventProjectGallery();

  if (!root) return;

  if (event.key === "Escape") {
    event.preventDefault();
    closeEventProjectGallery(root);
    return;
  }

  if (event.key !== "Tab") return;

  const panel = getEventProjectPanel(root);
  const focusable = getEventProjectFocusable(panel);

  if (!focusable.length) {
    event.preventDefault();
    panel?.focus();
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
});

const padHeroIndex = (value) => String(value).padStart(2, "0");

const initSignatureHeroSlider = (section) => {
  if (!section || section.dataset.heroSliderReady === "true") return;

  const slides = Array.from(section.querySelectorAll("[data-hero-slide]"));
  const currentEl = section.querySelector("[data-hero-counter-current]");
  const dragSurface = section.querySelector("[data-hero-drag]");
  const holdMs = Number(section.dataset.holdMs || 5000);
  const fadeMs = Number(section.dataset.fadeMs || 1000);

  section.dataset.heroSliderReady = "true";

  if (!slides.length) return;

  let index = Math.max(
    slides.findIndex((slide) => slide.classList.contains("is-active")),
    0,
  );
  let timer = null;
  let dragging = false;
  let startX = 0;
  let startY = 0;
  let lockAxis = "";

  const reduceMotion = () => isReducedMotion();

  const pauseButton = section.querySelector("[data-hero-pause]");
  let userPaused = false;
  let hoverPaused = false;

  const shouldAutoplay = () => !reduceMotion() && slides.length > 1 && !userPaused && !hoverPaused;

  const setActive = (next, instant = false) => {
    const total = slides.length;
    const target = ((next % total) + total) % total;
    const duration = instant || reduceMotion() ? 0 : fadeMs;

    slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === target;

      slide.style.transitionDuration = `${duration}ms`;
      slide.classList.toggle("is-active", isActive);
      slide.setAttribute("aria-hidden", String(!isActive));
    });

    index = target;

    if (currentEl) currentEl.textContent = padHeroIndex(index + 1);
  };

  const stopAutoplay = () => {
    window.clearTimeout(timer);
    timer = null;
  };

  const armAutoplay = () => {
    stopAutoplay();
    if (!shouldAutoplay()) return;

    timer = window.setTimeout(() => {
      setActive(index + 1, false);
      timer = window.setTimeout(armAutoplay, reduceMotion() ? 0 : fadeMs);
    }, holdMs);
  };

  const syncPauseButton = () => {
    if (!pauseButton) return;

    pauseButton.textContent = userPaused ? "Play slideshow" : "Pause slideshow";
    pauseButton.setAttribute("aria-pressed", String(userPaused));
  };

  const goBy = (step) => {
    stopAutoplay();
    setActive(index + step, reduceMotion());
    timer = window.setTimeout(armAutoplay, reduceMotion() ? 0 : fadeMs);
  };

  pauseButton?.addEventListener("click", () => {
    userPaused = !userPaused;
    hoverPaused = false;
    syncPauseButton();

    if (userPaused) stopAutoplay();
    else armAutoplay();
  });

  section.addEventListener("pointerenter", () => {
    hoverPaused = true;
    stopAutoplay();
  });

  section.addEventListener("pointerleave", () => {
    hoverPaused = false;
    if (!userPaused) armAutoplay();
  });

  section.addEventListener("focusin", () => {
    hoverPaused = true;
    stopAutoplay();
  });

  section.addEventListener("focusout", (event) => {
    if (section.contains(event.relatedTarget)) return;

    hoverPaused = false;
    if (!userPaused) armAutoplay();
  });

  section.addEventListener("keydown", (event) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    if (event.target.closest("input, textarea, select")) return;

    event.preventDefault();
    goBy(event.key === "ArrowRight" ? 1 : -1);
  });

  syncPauseButton();
  setActive(index, true);

  if (slides.length < 2 || !dragSurface) {
    armAutoplay();
    return;
  }

  dragSurface.addEventListener("pointerdown", (event) => {
    if (event.button !== 0) return;

    dragging = true;
    startX = event.clientX;
    startY = event.clientY;
    lockAxis = "";
    section.dataset.dragging = "true";
    dragSurface.setPointerCapture?.(event.pointerId);
    stopAutoplay();
  });

  dragSurface.addEventListener("pointermove", (event) => {
    if (!dragging) return;

    const deltaX = event.clientX - startX;
    const deltaY = event.clientY - startY;

    if (!lockAxis && Math.hypot(deltaX, deltaY) > 8) {
      lockAxis = Math.abs(deltaX) > Math.abs(deltaY) ? "x" : "y";
    }

    if (lockAxis === "x") event.preventDefault();
  });

  const endDrag = (event) => {
    if (!dragging) return;

    dragging = false;
    delete section.dataset.dragging;
    dragSurface.releasePointerCapture?.(event.pointerId);

    const deltaX = event.clientX - startX;

    if (lockAxis === "x" && Math.abs(deltaX) >= 48) {
      goBy(deltaX < 0 ? 1 : -1);
      return;
    }

    armAutoplay();
  };

  dragSurface.addEventListener("pointerup", endDrag);
  dragSurface.addEventListener("pointercancel", endDrag);

  armAutoplay();
};

const initSignatureHeroSliders = (root = document) => {
  if (root.matches?.("[data-signature-hero-slider]")) {
    initSignatureHeroSlider(root);
    return;
  }

  root.querySelectorAll("[data-signature-hero-slider]").forEach(initSignatureHeroSlider);
};

const initProductMarquee = (marquee) => {
  if (marquee.dataset.marqueeReady === "true") return;

  const track = marquee.querySelector("[data-marquee-track], [data-product-marquee-track]");
  const firstSet = marquee.querySelector("[data-marquee-set], [data-product-marquee-set]");

  if (!track || !firstSet) return;

  const motionPreference = window.matchMedia("(prefers-reduced-motion: reduce)");
  let frame = null;
  let previousTime = 0;
  let offset = 0;
  let setWidth = 0;

  const speed = Number(marquee.dataset.marqueeSpeed || 0.022);
  let currentSpeed = speed;
  let hoverPaused = false;
  let userPaused = false;
  let paused = false;
  const toggle = marquee.querySelector("[data-marquee-toggle]");
  const pauseLabel = toggle?.textContent.trim() || "Pause animation";
  const playLabel = pauseLabel.replace(/^Pause/i, "Play");

  const fillTrack = () => {
    track.querySelectorAll("[data-marquee-clone]").forEach((node) => node.remove());
    setWidth = firstSet.getBoundingClientRect().width;
    const viewport = marquee.clientWidth;

    if (!setWidth || !viewport) return;

    let trackWidth = setWidth;
    let copies = 1;

    while (trackWidth < viewport + setWidth && copies < 8) {
      const clone = firstSet.cloneNode(true);
      clone.setAttribute("aria-hidden", "true");
      clone.setAttribute("data-marquee-clone", "true");
      track.appendChild(clone);
      trackWidth += setWidth;
      copies += 1;
    }
  };

  const syncPaused = () => {
    paused = hoverPaused || userPaused;

    if (!toggle) return;

    toggle.textContent = userPaused ? playLabel : pauseLabel;
    toggle.setAttribute("aria-pressed", String(userPaused));
  };

  const measure = () => {
    fillTrack();
    if (setWidth) offset %= setWidth;
  };

  const stop = () => {
    if (frame) window.cancelAnimationFrame(frame);
    frame = null;
    previousTime = 0;
    track.style.transform = "";
  };

  const tick = (time) => {
    if (motionPreference.matches || !setWidth) {
      stop();
      return;
    }

    const delta = previousTime ? Math.min(time - previousTime, 64) : 0;
    previousTime = time;
    const targetSpeed = paused ? 0 : speed;
    currentSpeed += (targetSpeed - currentSpeed) * (1 - Math.exp(-delta / 240));
    if (paused && currentSpeed < 0.0001) currentSpeed = 0;
    offset = (offset + delta * currentSpeed) % setWidth;
    track.style.transform = `translate3d(${-offset}px, 0, 0)`;
    frame = window.requestAnimationFrame(tick);
  };

  const start = () => {
    measure();

    if (motionPreference.matches || !setWidth || frame) return;

    frame = window.requestAnimationFrame(tick);
  };

  marquee.dataset.marqueeReady = "true";
  start();
  syncPaused();

  toggle?.addEventListener("click", () => {
    userPaused = !userPaused;
    if (!userPaused) previousTime = 0;
    syncPaused();
  });

  if (marquee.hasAttribute("data-marquee-pause-hover")) {
    const hoverRoot = marquee.querySelector("[data-marquee-viewport]") || marquee;

    const pauseMotion = () => {
      hoverPaused = true;
      syncPaused();
    };

    const resumeMotion = (event) => {
      if (event?.relatedTarget && marquee.contains(event.relatedTarget)) return;

      hoverPaused = false;
      previousTime = 0;
      syncPaused();
    };

    hoverRoot.addEventListener("pointerenter", pauseMotion);
    hoverRoot.addEventListener("pointerleave", resumeMotion);
    marquee.addEventListener("focusin", pauseMotion);
    marquee.addEventListener("focusout", resumeMotion);
  }

  motionPreference.addEventListener?.("change", () => {
    if (motionPreference.matches) {
      stop();
      return;
    }

    start();
  });

  window.addEventListener(
    "resize",
    () => {
      measure();
    },
    { passive: true },
  );

  if (typeof ResizeObserver === "function") {
    const marqueeSizeObserver = new ResizeObserver(() => measure());
    marqueeSizeObserver.observe(marquee);
  }
};

const initProductMarquees = () => {
  document.querySelectorAll("[data-marquee], [data-product-marquee]").forEach(initProductMarquee);
};

const initStoryScroll = (section) => {
  if (!section || section.dataset.storyScrollReady === "true") return;

  const viewport = section.querySelector("[data-story-scroll-viewport]");
  const track = section.querySelector("[data-story-scroll-track]");
  const set = section.querySelector("[data-story-scroll-set]");

  if (!viewport || !track || !set) return;

  section.dataset.storyScrollReady = "true";

  const abort = new AbortController();
  const { signal } = abort;
  const pauseButton = section.querySelector("[data-story-scroll-pause]");
  const prevButton = section.querySelector("[data-story-scroll-prev]");
  const nextButton = section.querySelector("[data-story-scroll-next]");
  const pauseLabel = pauseButton?.textContent.trim() || "Pause story scroll";
  const playLabel = pauseLabel.replace(/^Pause/i, "Play");
  const motionPreference = window.matchMedia("(prefers-reduced-motion: reduce)");
  const mobileLayout = window.matchMedia("(max-width: 640px)");
  const parsedSpeed = Number.parseFloat(section.dataset.storySpeed);
  const marqueeSpeed = Number.isFinite(parsedSpeed) ? Math.min(60, Math.max(15, parsedSpeed)) : 25;

  let frame = 0;
  let measureFrame = 0;
  let lastTime = 0;
  let offset = 0;
  let positions = [0];
  let loopWidth = 0;
  let looping = false;
  let inView = true;
  let hoverPaused = false;
  let focusPaused = false;
  let userPaused = false;

  const syncPauseButton = () => {
    if (!pauseButton) return;

    pauseButton.textContent = userPaused ? playLabel : pauseLabel;
    pauseButton.setAttribute("aria-pressed", String(userPaused));
  };

  const syncArrows = () => {
    const disabled = !looping;

    prevButton?.toggleAttribute("disabled", disabled);
    nextButton?.toggleAttribute("disabled", disabled);
  };

  const canRun = () =>
    looping && inView && !hoverPaused && !focusPaused && !userPaused && !document.hidden && track.isConnected;

  const apply = () => {
    if (!looping) {
      track.style.transition = "";
      track.style.transform = "";
      return;
    }

    track.style.transition = "none";
    track.style.transform = `translate3d(${-offset}px, 0, 0)`;
  };

  const stopFrame = () => {
    window.cancelAnimationFrame(frame);
    frame = 0;
    lastTime = 0;
  };

  const tick = (now) => {
    if (!canRun() || !loopWidth) {
      stopFrame();
      return;
    }

    const delta = lastTime ? now - lastTime : 0;

    lastTime = now;
    offset = (offset + (marqueeSpeed * delta) / 1000) % loopWidth;
    apply();
    frame = window.requestAnimationFrame(tick);
  };

  const start = () => {
    if (frame || !canRun()) return;

    lastTime = 0;
    frame = window.requestAnimationFrame(tick);
  };

  const clearClone = () => {
    track.querySelector("[data-story-scroll-clone]")?.remove();
  };

  const measure = () => {
    const previous = offset;

    stopFrame();
    clearClone();
    track.style.transition = "none";
    track.style.transform = "";

    const slides = [...set.querySelectorAll(":scope > .athoor-story-slider__slide")];
    const setLeft = set.offsetLeft;
    const nextPositions = [];

    slides.forEach((slide) => {
      const position = Math.max(slide.offsetLeft - setLeft, 0);

      if (!nextPositions.length || nextPositions[nextPositions.length - 1] !== position) nextPositions.push(position);
    });

    positions = nextPositions.length ? nextPositions : [0];
    looping =
      !motionPreference.matches &&
      !mobileLayout.matches &&
      positions.length > 1 &&
      set.offsetWidth > viewport.clientWidth;

    if (looping) {
      const clone = set.cloneNode(true);

      clone.setAttribute("aria-hidden", "true");
      clone.setAttribute("data-story-scroll-clone", "true");
      clone.inert = true;
      clone.querySelectorAll("[id]").forEach((element) => element.removeAttribute("id"));
      clone
        .querySelectorAll("[data-shopify-editor-block]")
        .forEach((element) => element.removeAttribute("data-shopify-editor-block"));
      clone.querySelectorAll("img").forEach((image) => {
        image.alt = "";
      });
      track.append(clone);

      const cloneSlide = clone.querySelector(".athoor-story-slider__slide");

      loopWidth = cloneSlide ? Math.max(cloneSlide.offsetLeft - set.offsetLeft, 0) : set.offsetWidth;
      if (!loopWidth) looping = false;
      offset = loopWidth ? previous % loopWidth : 0;
    } else {
      loopWidth = 0;
      offset = 0;
    }

    section.classList.toggle("is-marquee-ready", looping);
    apply();
    syncArrows();
    syncPauseButton();
    start();
  };

  const scheduleMeasure = () => {
    if (measureFrame) return;

    measureFrame = window.requestAnimationFrame(() => {
      measureFrame = 0;
      if (signal.aborted) return;

      measure();
    });
  };

  const step = (direction) => {
    if (!looping || !loopWidth) return;

    if (direction > 0) {
      const next = positions.find((position) => position > offset + 1);

      offset = next == null ? 0 : next;
    } else {
      const previousPosition = [...positions].reverse().find((position) => position < offset - 1);

      offset = previousPosition == null ? positions[positions.length - 1] || 0 : previousPosition;
    }

    apply();
    start();
  };

  section._storyScrollStop = () => {
    abort.abort();
    stopFrame();
    window.cancelAnimationFrame(measureFrame);
    clearClone();
    section.classList.remove("is-marquee-ready");
    track.style.transition = "";
    track.style.transform = "";
  };

  viewport.addEventListener(
    "pointerenter",
    (event) => {
      if (event.pointerType === "touch") return;

      hoverPaused = true;
      stopFrame();
    },
    { signal },
  );

  viewport.addEventListener(
    "pointerleave",
    (event) => {
      if (event.pointerType === "touch") return;

      hoverPaused = false;
      start();
    },
    { signal },
  );

  section.addEventListener(
    "focusin",
    () => {
      window.requestAnimationFrame(() => {
        if (signal.aborted) return;

        focusPaused = Boolean(
          section.contains(document.activeElement) && document.activeElement.matches(":focus-visible"),
        );
        if (focusPaused) stopFrame();
        else start();
      });
    },
    { signal },
  );

  section.addEventListener(
    "focusout",
    (event) => {
      if (section.contains(event.relatedTarget)) return;

      focusPaused = false;
      start();
    },
    { signal },
  );

  prevButton?.addEventListener("click", () => step(-1), { signal });
  nextButton?.addEventListener("click", () => step(1), { signal });
  pauseButton?.addEventListener(
    "click",
    () => {
      userPaused = !userPaused;
      syncPauseButton();
      if (userPaused) stopFrame();
      else start();
    },
    { signal },
  );

  motionPreference.addEventListener?.("change", scheduleMeasure, { signal });
  mobileLayout.addEventListener?.("change", scheduleMeasure, { signal });
  window.addEventListener("resize", scheduleMeasure, { passive: true, signal });
  document.addEventListener(
    "visibilitychange",
    () => {
      if (document.hidden) stopFrame();
      else start();
    },
    { signal },
  );

  if (typeof IntersectionObserver === "function") {
    const visibility = new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting;
        if (inView) start();
        else stopFrame();
      },
      { rootMargin: "100px" },
    );

    visibility.observe(section);
    signal.addEventListener("abort", () => visibility.disconnect());
  }

  if (typeof ResizeObserver === "function") {
    const sizes = new ResizeObserver(scheduleMeasure);

    sizes.observe(viewport);
    sizes.observe(set);
    signal.addEventListener("abort", () => sizes.disconnect());
  }

  measure();
};

const initStoryScrolls = (root = document) => {
  const nodes = [];

  if (root.matches?.("[data-story-scroll]")) nodes.push(root);
  root.querySelectorAll?.("[data-story-scroll]").forEach((node) => nodes.push(node));
  nodes.forEach(initStoryScroll);
};

const stopStoryScroll = (root) => {
  const section = root?.matches?.("[data-story-scroll]") ? root : root?.querySelector?.("[data-story-scroll]");

  section?._storyScrollStop?.();
};

const PRODUCT_BUY_FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

let productBuyLastFocus = null;

const getProductBuyRoot = (node) => node?.closest?.("[data-product-buy]");

const getProductBuyPanel = (root) => root?.querySelector("[data-product-buy-panel]");

const getProductBuyFocusable = (panel) =>
  [...(panel?.querySelectorAll(PRODUCT_BUY_FOCUSABLE) || [])].filter((element) => !element.closest("[inert]"));

const setProductBuyPageInert = (root, open) => {
  document.querySelector("[data-header]")?.toggleAttribute("inert", open);
  document.querySelector(".shopify-section-group-footer-group")?.toggleAttribute("inert", open);

  const productSection = root.closest(".shopify-section") || root;

  document.querySelectorAll("#MainContent > *").forEach((child) => {
    if (child === productSection) return;
    child.toggleAttribute("inert", open);
  });

  [...root.children].forEach((child) => {
    if (child.classList.contains("athoor-modal--product-buy")) return;
    child.toggleAttribute("inert", open);
  });
};

const syncProductBuyDialog = (root) => {
  if (!root) return;

  const open = root.dataset.buyOpen === "true";
  const panel = getProductBuyPanel(root);

  panel?.toggleAttribute("inert", !open);
  panel?.setAttribute("aria-hidden", open ? "false" : "true");
  root.querySelector(".athoor-modal--product-buy .athoor-modal__backdrop")?.toggleAttribute("inert", !open);
};

const setProductBuyOpen = (root, open) => {
  if (!root) return;

  root.dataset.buyOpen = open ? "true" : "false";
  setProductBuyPageInert(root, open);
  syncProductBuyDialog(root);
  document.body.classList.toggle("athoor-product-buy-modal-open", open);

  if (open) {
    const panel = getProductBuyPanel(root);
    const focusable = getProductBuyFocusable(panel);
    (focusable[0] || panel)?.focus();
    return;
  }

  if (productBuyLastFocus?.isConnected) productBuyLastFocus.focus();
  productBuyLastFocus = null;
};

const initProductBuyDialogs = (scope = document) => {
  const roots = scope.matches?.("[data-product-buy]")
    ? [scope]
    : [...(scope.querySelectorAll?.("[data-product-buy]") || [])];

  roots.forEach((root) => setProductBuyOpen(root, false));
};

document.addEventListener("click", (event) => {
  const openTrigger = event.target.closest("[data-product-buy-open]");

  if (openTrigger) {
    const root = getProductBuyRoot(openTrigger);

    if (!root) return;

    productBuyLastFocus = openTrigger;
    setProductBuyOpen(root, true);
    return;
  }

  const closeTrigger = event.target.closest("[data-product-buy-close]");

  if (!closeTrigger) return;

  const root = getProductBuyRoot(closeTrigger);

  if (root) setProductBuyOpen(root, false);
});

document.addEventListener("keydown", (event) => {
  const root = document.querySelector('[data-product-buy][data-buy-open="true"]');

  if (!root) return;

  if (event.key === "Escape") {
    event.preventDefault();
    setProductBuyOpen(root, false);
    return;
  }

  if (event.key !== "Tab") return;

  const panel = getProductBuyPanel(root);
  const focusable = getProductBuyFocusable(panel);

  if (!focusable.length) {
    event.preventDefault();
    panel?.focus();
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
});

const clampNumber = (value, min, max) => Math.min(Math.max(value, min), max);
const easeEventReveal = (progress) => 1 - Math.pow(1 - progress, 3);
const eventRevealState = new WeakMap();

const updatePartnerReveal = () => {
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  document.querySelectorAll("[data-partner-reveal]").forEach((section) => {
    const floats = section.querySelectorAll("[data-partner-reveal-float]");
    const scrollArea = section.querySelector(".athoor-partner-reveal__scroll") || section;
    const room = section.querySelector("[data-partner-reveal-room]");

    if (isReducedMotion()) {
      floats.forEach((item) => {
        item.style.setProperty("--athoor-partner-drift", "translate3d(0, 0, 0)");
        item.style.setProperty("--athoor-partner-card-opacity", "1");
      });
      section.querySelector(".athoor-partner-reveal__canvas")?.style.removeProperty("height");
      if (room) {
        section.style.setProperty("--athoor-partner-room-progress", "1");
        section.style.setProperty("--athoor-partner-room-scale", "1");
        section.style.setProperty("--athoor-partner-room-radius", "0px");
        section.style.setProperty("--athoor-partner-room-image-scale", "1");
        section.style.setProperty("--athoor-partner-room-shadow-opacity", "0");
        if (window.innerWidth >= 1200) {
          section
            .querySelector(".athoor-partner-reveal__preview--8")
            ?.style.setProperty("--athoor-partner-card-opacity", "0");
        }
      }
      return;
    }

    const rect = scrollArea.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const compactScreen = viewportWidth < 1200;
    const smallScreen = viewportWidth < 481;
    const driftScale = viewportWidth >= 1200 ? 1 : smallScreen ? 0.72 : 0.82;

    floats.forEach((item) => {
      const itemProgress = clampNumber(
        (viewportHeight * 0.95 - (rect.top + item.offsetTop)) / (viewportHeight * (compactScreen ? 0.68 : 0.78)),
        0,
        1,
      );
      const easedProgress = itemProgress * itemProgress * (3 - 2 * itemProgress);
      const direction = 1 - easedProgress;
      const driftX = Number(item.dataset.driftX || 0) * driftScale;
      const driftY = Number(item.dataset.driftY || 0) * driftScale;
      const depth = Number(item.dataset.driftDepth || 1);
      const x = driftX * direction * depth;
      const y = driftY * direction * depth;

      item.style.setProperty("--athoor-partner-drift", `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0)`);
      item.style.setProperty("--athoor-partner-card-opacity", (0.72 + 0.28 * easedProgress).toFixed(3));
    });

    if (room) {
      const initialScale = compactScreen ? (smallScreen ? 0.36 : 0.42) : 0.32;
      const roomTop = room.getBoundingClientRect().top;
      const startAt = viewportHeight * (compactScreen ? 0.95 : 0.64);
      const travel = compactScreen
        ? Math.max(smallScreen ? 440 : 520, viewportHeight * (smallScreen ? 0.68 : 0.72))
        : Math.max(680, viewportHeight * 0.95);
      const reveal = clampNumber((startAt - roomTop) / travel, 0, 1);
      const easedReveal = reveal * reveal * (3 - 2 * reveal);
      const scale = initialScale + (1 - initialScale) * easedReveal;
      const radius = (16 / initialScale) * (1 - easedReveal);
      const imageScale = compactScreen ? 1 : 1.06 - 0.06 * easedReveal;
      const shadowOpacity = compactScreen ? 0 : 0.16 * (1 - easedReveal);

      section.style.setProperty("--athoor-partner-room-progress", easedReveal.toFixed(4));
      section.style.setProperty("--athoor-partner-room-scale", scale.toFixed(4));
      section.style.setProperty("--athoor-partner-room-radius", `${radius.toFixed(2)}px`);
      section.style.setProperty("--athoor-partner-room-image-scale", imageScale.toFixed(4));
      section.style.setProperty("--athoor-partner-room-shadow-opacity", shadowOpacity.toFixed(4));

      const canvas = section.querySelector(".athoor-partner-reveal__canvas");

      if (smallScreen && canvas && room.offsetHeight) {
        canvas.style.height = `${Math.ceil(room.offsetTop + room.offsetHeight * scale)}px`;
      } else {
        canvas?.style.removeProperty("height");
      }

      if (!compactScreen) {
        const finalCard = section.querySelector(".athoor-partner-reveal__preview--8");
        finalCard?.style.setProperty("--athoor-partner-card-opacity", (1 - easedReveal).toFixed(3));
      }
    }
  });
};

let partnerRevealFrame = null;

const schedulePartnerRevealUpdate = () => {
  if (partnerRevealFrame) return;

  partnerRevealFrame = window.requestAnimationFrame(() => {
    partnerRevealFrame = null;
    updatePartnerReveal();
  });
};

const updateEventStacks = () => {
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  let shouldContinue = false;

  document.querySelectorAll("[data-events-stack]").forEach((stack) => {
    const cards = Array.from(stack.querySelectorAll("[data-event-card]"));

    stack.dataset.eventsStackReady = "true";

    cards.forEach((card, index) => {
      if (index === 0 || isReducedMotion()) {
        card.style.setProperty("--athoor-event-reveal", "0%");
        return;
      }

      const previousReveal = eventRevealState.has(card) ? eventRevealState.get(card) : 100;
      const slidePx = (previousReveal / 100) * (card.offsetHeight || viewportHeight);
      const layoutTop = card.getBoundingClientRect().top - slidePx;
      const rawProgress = clampNumber((viewportHeight - layoutTop) / (viewportHeight * 1.18), 0, 1);
      const easedProgress = easeEventReveal(rawProgress);
      const targetReveal = (1 - easedProgress) * 100;
      const revealDelta = targetReveal - previousReveal;
      const reveal = Math.abs(revealDelta) < 0.08 ? targetReveal : previousReveal + revealDelta * 0.22;

      if (Math.abs(targetReveal - reveal) >= 0.08) {
        shouldContinue = true;
      }

      card.style.setProperty("--athoor-event-reveal", `${reveal}%`);
      eventRevealState.set(card, reveal);
    });

    cards.forEach((card, index) => {
      if (isReducedMotion() || !cards[index + 1]) {
        card.style.setProperty("--athoor-event-cover", "0");
        return;
      }

      const nextReveal = eventRevealState.has(cards[index + 1]) ? eventRevealState.get(cards[index + 1]) : 100;
      const cover = clampNumber(1 - nextReveal / 100, 0, 1);

      card.style.setProperty("--athoor-event-cover", cover.toFixed(3));
    });
  });

  if (shouldContinue) {
    scheduleEventStackUpdate();
  }
};

let eventStackFrame = null;

const scheduleEventStackUpdate = () => {
  if (eventStackFrame) return;

  eventStackFrame = window.requestAnimationFrame(() => {
    eventStackFrame = null;
    updateEventStacks();
  });
};

const collectionSectionSelector = "[data-collection-section]";
const collectionSliderSelector = "[data-collection-slider]";
let collectionFilterRequest = null;
let collectionInfiniteRequest = null;
let collectionInfiniteObserver = null;

const getCollectionSection = () => document.querySelector(collectionSectionSelector);
const getCollectionSlider = () => document.querySelector(collectionSliderSelector);

const finishCollectionInfiniteLoading = (loadRoot, message = "You’ve viewed all products") => {
  if (!loadRoot) return;

  loadRoot.classList.remove("is-loading", "has-error");
  loadRoot.classList.add("is-complete");
  loadRoot.dataset.nextUrl = "";
  loadRoot.querySelector("[data-collection-load-progress]")?.setAttribute("aria-hidden", "true");

  const status = loadRoot.querySelector("[data-collection-load-message]");
  if (status) status.textContent = message;
};

const loadNextCollectionPage = async (loadRoot) => {
  const section = loadRoot?.closest(collectionSectionSelector);
  const nextUrl = loadRoot?.dataset.nextUrl;

  if (!section || !nextUrl || loadRoot.classList.contains("is-loading")) return;

  collectionInfiniteObserver?.unobserve(loadRoot);
  collectionInfiniteRequest?.abort();
  collectionInfiniteRequest = new AbortController();

  loadRoot.classList.remove("has-error");
  loadRoot.classList.add("is-loading");
  loadRoot.querySelector("[data-collection-load-progress]")?.removeAttribute("aria-hidden");
  loadRoot.querySelector("[data-collection-load-retry]")?.setAttribute("hidden", "");

  const status = loadRoot.querySelector("[data-collection-load-message]");
  if (status) status.textContent = "Loading more products";

  const requestUrl = new URL(nextUrl, window.location.origin);
  requestUrl.searchParams.set("section_id", section.dataset.sectionId);

  try {
    const response = await fetch(requestUrl, { signal: collectionInfiniteRequest.signal });

    if (!response.ok) throw new Error("Collection page request failed");

    const html = await response.text();
    const nextSection = parseSectionHtml(html, section.dataset.sectionId, collectionSectionSelector);
    const currentGrid = section.querySelector(".athoor-collection__grid");
    const nextGrid = nextSection?.querySelector(".athoor-collection__grid");

    if (!nextSection || !currentGrid || !nextGrid) throw new Error("Collection products were not found");

    const appendedBands = [...nextGrid.children];
    const fragment = document.createDocumentFragment();
    appendedBands.forEach((band) => fragment.append(band));
    currentGrid.append(fragment);

    const currentPagination = section.querySelector("[data-collection-pagination]");
    const nextPagination = nextSection.querySelector("[data-collection-pagination]");

    if (nextPagination) {
      nextPagination.setAttribute("inert", "");
      nextPagination.setAttribute("aria-hidden", "true");
    }

    if (currentPagination && nextPagination) currentPagination.replaceWith(nextPagination);
    else currentPagination?.remove();

    const nextLoadRoot = nextSection.querySelector("[data-collection-load]");
    loadRoot.dataset.nextUrl = nextLoadRoot?.dataset.nextUrl || "";
    loadRoot.classList.remove("is-loading");
    loadRoot.querySelector("[data-collection-load-progress]")?.setAttribute("aria-hidden", "true");

    appendedBands.forEach((band) => initProductCardReveal(band));
    scheduleCollectionGradientUpdate();

    if (loadRoot.dataset.nextUrl) {
      if (status) status.textContent = "";
      collectionInfiniteObserver?.observe(loadRoot);
    } else {
      finishCollectionInfiniteLoading(loadRoot);
    }
  } catch (error) {
    if (error.name === "AbortError") return;

    loadRoot.classList.remove("is-loading");
    loadRoot.classList.add("has-error");
    loadRoot.querySelector("[data-collection-load-progress]")?.setAttribute("aria-hidden", "true");
    loadRoot.querySelector("[data-collection-load-retry]")?.removeAttribute("hidden");
    if (status) status.textContent = "We couldn’t load more products.";
  } finally {
    collectionInfiniteRequest = null;
  }
};

const initCollectionInfiniteLoading = (scope = document) => {
  const loadRoots = scope.matches?.("[data-collection-load]")
    ? [scope]
    : [...scope.querySelectorAll("[data-collection-load]")];

  if (!loadRoots.length) return;

  if (!("IntersectionObserver" in window)) {
    loadRoots.forEach((loadRoot) => {
      if (!loadRoot.dataset.nextUrl) return;
      loadRoot.classList.add("has-error");
      const status = loadRoot.querySelector("[data-collection-load-message]");
      if (status) status.textContent = "Load more products";
      loadRoot.querySelector("[data-collection-load-retry]")?.removeAttribute("hidden");
    });
    return;
  }

  if (!collectionInfiniteObserver) {
    collectionInfiniteObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) loadNextCollectionPage(entry.target);
        });
      },
      { rootMargin: "0px 0px 700px 0px" },
    );
  }

  loadRoots.forEach((loadRoot) => {
    const pagination = loadRoot.closest(collectionSectionSelector)?.querySelector("[data-collection-pagination]");
    pagination?.setAttribute("inert", "");
    pagination?.setAttribute("aria-hidden", "true");

    collectionInfiniteObserver.unobserve(loadRoot);
    if (loadRoot.dataset.nextUrl) collectionInfiniteObserver.observe(loadRoot);
  });
};

const isPlainPrimaryNavigation = (event, link) =>
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey &&
  !link.hasAttribute("download") &&
  (!link.target || link.target === "_self");

const savePartialNavigationScroll = () => {
  const state = window.history.state && typeof window.history.state === "object" ? window.history.state : {};

  window.history.replaceState(
    { ...state, athoorScroll: { x: window.scrollX, y: window.scrollY } },
    "",
    window.location.href,
  );
};

const pushPartialNavigationState = (url) => {
  window.history.pushState(
    { athoorPartialNavigation: true, athoorScroll: { x: window.scrollX, y: window.scrollY } },
    "",
    url.href,
  );
};

const restorePartialNavigationScroll = (position) => {
  if (!position || !Number.isFinite(position.x) || !Number.isFinite(position.y)) return;

  const restore = () => window.scrollTo(position.x, position.y);

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(restore);
  });
  window.setTimeout(restore, 150);
};

const syncMetaContent = (selector, attribute, value, content) => {
  let element = document.head.querySelector(selector);

  if (!content) {
    element?.remove();
    return;
  }

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, value);
    document.head.append(element);
  }

  element.setAttribute("content", content);
};

const syncPartialNavigationMetadata = (root, url) => {
  if (!root) return;

  const title = root.dataset.documentTitle?.trim();
  const description = root.dataset.documentDescription?.trim() || "";
  const canonicalUrl = root.dataset.documentCanonicalUrl || url.href;

  if (title) document.title = title;

  let canonical = document.head.querySelector('link[rel="canonical"]');

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.append(canonical);
  }

  canonical.href = canonicalUrl;
  syncMetaContent('meta[name="description"]', "name", "description", description);

  document.head.querySelector('meta[property="og:title"]')?.setAttribute("content", title || document.title);
  document.head.querySelector('meta[property="og:description"]')?.setAttribute("content", description);
  document.head.querySelector('meta[property="og:url"]')?.setAttribute("content", canonicalUrl);
  document.head.querySelector('meta[name="twitter:title"]')?.setAttribute("content", title || document.title);
  document.head.querySelector('meta[name="twitter:description"]')?.setAttribute("content", description);
};

const getPartialNavigationAnnouncer = () => {
  let announcer = document.querySelector("[data-partial-navigation-announcer]");

  if (announcer) return announcer;

  announcer = document.createElement("p");
  announcer.className = "visually-hidden";
  announcer.dataset.partialNavigationAnnouncer = "";
  announcer.setAttribute("role", "status");
  announcer.setAttribute("aria-live", "polite");
  announcer.setAttribute("aria-atomic", "true");
  document.body.append(announcer);

  return announcer;
};

const announcePartialNavigation = (root) => {
  const heading = root?.querySelector("h1")?.textContent?.trim();
  const announcer = getPartialNavigationAnnouncer();

  announcer.textContent = "";
  window.requestAnimationFrame(() => {
    announcer.textContent = heading ? `${heading} loaded` : "Page content updated";
  });
};

const focusPartialNavigationHeading = (root) => {
  const heading = root?.querySelector("h1");

  if (!heading) return;

  heading.setAttribute("tabindex", "-1");
  heading.focus({ preventScroll: true });
};

const publishPartialNavigation = (url, resourceType) => {
  const detail = { url: url.href, title: document.title, resourceType };

  window.dispatchEvent(new CustomEvent("athoor:page-view", { detail }));

  if (window.Shopify?.analytics?.publish) {
    try {
      const publication = window.Shopify.analytics.publish("athoor:partial_page_viewed", detail);
      publication?.catch?.(() => {});
    } catch (_error) {
      // Navigation must remain independent of optional analytics integrations.
    }
  }
};

const completePartialNavigation = (root, url, options = {}) => {
  if (options.shouldPushState) pushPartialNavigationState(url);

  syncPartialNavigationMetadata(root, url);
  announcePartialNavigation(root);

  if (options.focusHeading) focusPartialNavigationHeading(root);
  if (options.restoreScroll) restorePartialNavigationScroll(options.restoreScroll);

  publishPartialNavigation(url, options.resourceType || "page");
};

const parseSectionHtml = (html, sectionId, fallbackSelector) => {
  const container = document.createElement("div");
  container.innerHTML = html;

  return container.querySelector(`[data-section-id="${sectionId}"]`) || container.querySelector(fallbackSelector);
};

const prepareOpenFilterModal = (root) => {
  const form = root?.querySelector("[data-collection-filter-form]");
  const sidebar = root?.querySelector("[data-sticky-filter-sidebar]");
  const backdrop = root?.querySelector(".athoor-collection-filters__backdrop");

  if (!form || !sidebar) return;

  form.dataset.filterOpen = "open";
  form.dataset.filterModal = "open";
  form.dataset.filterInstant = "true";
  sidebar.removeAttribute("inert");
  backdrop?.removeAttribute("inert");
};

const filterGroupLabel = (group) =>
  group.querySelector(".athoor-collection-filters__summary span")?.textContent?.trim();

const preserveOpenFilterGroups = (currentGroups, nextGroups) => {
  const openLabels = new Set(
    [...currentGroups.querySelectorAll("details[open]")].map(filterGroupLabel).filter(Boolean),
  );

  nextGroups.querySelectorAll("details").forEach((group) => {
    if (openLabels.has(filterGroupLabel(group))) group.open = true;
  });
};

const collectionFilterFocusSelector = (root) => {
  const active = document.activeElement;

  if (!(active instanceof HTMLInputElement) || !root.contains(active) || !active.name) return "";

  const name = `[name="${CSS.escape(active.name)}"]`;

  if (active.type === "checkbox" || active.type === "radio") {
    return `${name}[value="${CSS.escape(active.value)}"]`;
  }

  return name;
};

const patchOpenCollectionFilters = (current, next) => {
  const currentResults = current.querySelector("[data-collection-results]");
  const nextResults = next.querySelector("[data-collection-results]");
  const currentGroups = current.querySelector("[data-filter-groups]");
  const nextGroups = next.querySelector("[data-filter-groups]");
  const currentHeaderActions = current.querySelector("[data-filter-header-actions]");
  const nextHeaderActions = next.querySelector("[data-filter-header-actions]");
  const focusSelector = collectionFilterFocusSelector(current);

  if (!currentResults || !nextResults || !currentGroups || !nextGroups) return null;

  if (currentHeaderActions && nextHeaderActions) currentHeaderActions.replaceWith(nextHeaderActions);
  preserveOpenFilterGroups(currentGroups, nextGroups);
  currentGroups.replaceWith(nextGroups);
  currentResults.replaceWith(nextResults);

  if (focusSelector) current.querySelector(focusSelector)?.focus({ preventScroll: true });

  return current;
};

const swapCollectionNode = (current, html, fallbackSelector, { keepFilterModal = false } = {}) => {
  if (!current || !html) return current;

  const next = parseSectionHtml(html, current.dataset.sectionId, fallbackSelector);

  if (!next) return null;

  if (keepFilterModal) {
    const patched = patchOpenCollectionFilters(current, next);

    if (patched) return patched;

    prepareOpenFilterModal(next);
  }

  current.replaceWith(next);
  return next;
};

const scrollToCollectionProducts = (section) => {
  const target = section.querySelector(".athoor-collection__grid, .athoor-collection__empty");

  if (!target) return;

  const top = target.getBoundingClientRect().top + window.scrollY - 24;

  window.scrollTo({
    top: Math.max(top, 0),
    behavior: isReducedMotion() ? "auto" : "smooth",
  });
};

const resetStickyCollectionFilter = (sidebar) => {
  sidebar.classList.remove("is-filter-fixed", "is-filter-bottom");
  sidebar.style.removeProperty("left");
  sidebar.style.removeProperty("top");
  sidebar.style.removeProperty("width");
};

const updateStickyCollectionFilters = () => {
  document.querySelectorAll("[data-sticky-filter-boundary]").forEach((boundary) => {
    const cell = boundary.querySelector("[data-sticky-filter-cell]");
    const sidebar = boundary.querySelector("[data-sticky-filter-sidebar]");

    if (cell) cell.style.removeProperty("min-height");
    if (sidebar) resetStickyCollectionFilter(sidebar);
  });
};

let stickyCollectionFilterFrame = null;

const scheduleStickyCollectionFilterUpdate = () => {
  if (stickyCollectionFilterFrame) return;

  stickyCollectionFilterFrame = window.requestAnimationFrame(() => {
    stickyCollectionFilterFrame = null;
    updateStickyCollectionFilters();
  });
};

const buildCollectionFilterUrl = (form) => {
  const url = new URL(form.action, window.location.origin);
  const formData = new FormData(form);

  formData.forEach((value, key) => {
    if (value === "") return;
    url.searchParams.append(key, value);
  });

  return url;
};

const renderCollectionSection = (url, shouldPushState = true, options = {}) => {
  const section = getCollectionSection();
  const slider = getCollectionSlider();

  if (!section || !section.dataset.sectionId) {
    window.location.href = url.href;
    return;
  }

  if (options.sliderDirection && slider?.dataset.sliding === "true") return;

  if (shouldPushState) savePartialNavigationScroll();
  if (collectionFilterRequest) collectionFilterRequest.abort();
  if (collectionInfiniteRequest) collectionInfiniteRequest.abort();

  collectionFilterRequest = new AbortController();

  const requestUrl = new URL(url.href);
  const sliderId = slider?.dataset.sectionId;
  const useBundle = Boolean(sliderId);

  requestUrl.searchParams.delete("section_id");
  requestUrl.searchParams.delete("sections");

  if (useBundle) {
    requestUrl.searchParams.set("sections", `${section.dataset.sectionId},${sliderId}`);
  } else {
    requestUrl.searchParams.set("section_id", section.dataset.sectionId);
  }

  section.setAttribute("aria-busy", "true");
  if (!collectionFilterOpen) section.classList.add("is-loading");

  if (options.sliderDirection && slider) {
    slider.dataset.sliding = "true";
  }

  const sliderMotion = slideTitleSlider(slider, options.sliderDirection);

  fetch(requestUrl, { signal: collectionFilterRequest.signal })
    .then((response) => {
      if (!response.ok) throw new Error("Collection section request failed");
      if (useBundle) return response.json();
      return response.text().then((html) => ({ [section.dataset.sectionId]: html }));
    })
    .then(async (sections) => {
      await sliderMotion;

      const nextSection = swapCollectionNode(section, sections[section.dataset.sectionId], collectionSectionSelector, {
        keepFilterModal: collectionFilterOpen,
      });

      if (!nextSection) {
        window.location.href = url.href;
        return;
      }

      let nextSlider = slider;

      if (slider && sliderId) {
        nextSlider = swapCollectionNode(slider, sections[sliderId], collectionSliderSelector);
      }

      scheduleStickyCollectionFilterUpdate();
      syncCollectionFilterModal({ open: collectionFilterOpen });
      getCollectionFilterForm()?.removeAttribute("data-filter-instant");
      initEventSliders();
      initProductCardReveal(nextSection);
      initCollectionInfiniteLoading(nextSection);
      scheduleCollectionGradientUpdate();

      completePartialNavigation(nextSlider || nextSection, url, {
        shouldPushState,
        restoreScroll: options.restoreScroll,
        focusHeading: options.focusHeading,
        resourceType: "collection",
      });

      if (options.scrollToProducts && !options.restoreScroll) {
        window.requestAnimationFrame(() => scrollToCollectionProducts(nextSection));
      }
    })
    .catch((error) => {
      if (error.name === "AbortError") {
        if (slider?.isConnected) positionEventSlider(slider);
        return;
      }
      window.location.href = url.href;
    })
    .finally(() => {
      collectionFilterRequest = null;
      getCollectionSection()?.removeAttribute("aria-busy");
      getCollectionSection()?.classList.remove("is-loading");
      if (slider?.isConnected) slider.removeAttribute("data-sliding");
    });
};

const eventSliderSelector = "[data-event-slider]";
const eventGallerySelector = "[data-event-gallery]";
let eventPageRequest = null;

const getEventSlider = () => document.querySelector(eventSliderSelector);
const getEventGallery = () => document.querySelector(eventGallerySelector);

const renderEventPage = (url, shouldPushState = true, options = {}) => {
  const slider = getEventSlider();
  const gallery = getEventGallery();

  if (gallery?.dataset.projectOpen === "true") closeEventProjectGallery(gallery, false);

  if (!slider?.dataset.sectionId) {
    window.location.href = url.href;
    return;
  }

  if (options.sliderDirection && slider.dataset.sliding === "true") return;

  if (shouldPushState) savePartialNavigationScroll();
  if (eventPageRequest) eventPageRequest.abort();

  eventPageRequest = new AbortController();

  const requestUrl = new URL(url.href);
  const sliderId = slider.dataset.sectionId;
  const galleryId = gallery?.dataset.sectionId;
  const sectionIds = galleryId ? `${sliderId},${galleryId}` : sliderId;

  requestUrl.searchParams.delete("section_id");
  requestUrl.searchParams.delete("sections");
  requestUrl.searchParams.set("sections", sectionIds);

  slider.setAttribute("aria-busy", "true");

  if (options.sliderDirection) {
    slider.dataset.sliding = "true";
  }

  const sliderMotion = slideTitleSlider(slider, options.sliderDirection);

  fetch(requestUrl, { signal: eventPageRequest.signal })
    .then((response) => {
      if (!response.ok) throw new Error("Event page request failed");
      return response.json();
    })
    .then(async (sections) => {
      await sliderMotion;

      const nextSlider = swapCollectionNode(slider, sections[sliderId], eventSliderSelector);

      if (!nextSlider) {
        window.location.href = url.href;
        return;
      }

      let nextGallery = null;

      if (gallery && galleryId) {
        nextGallery = swapCollectionNode(gallery, sections[galleryId], eventGallerySelector);

        if (nextGallery) initEventGallery(nextGallery);
      }

      initEventSliders();
      initPremiumScrollReveals(nextSlider);
      if (nextGallery) initPremiumScrollReveals(nextGallery);
      completePartialNavigation(nextSlider, url, {
        shouldPushState,
        restoreScroll: options.restoreScroll,
        focusHeading: options.focusHeading,
        resourceType: "event",
      });
    })
    .catch((error) => {
      if (error.name === "AbortError") {
        if (slider?.isConnected) positionEventSlider(slider);
        return;
      }
      window.location.href = url.href;
    })
    .finally(() => {
      eventPageRequest = null;
      getEventSlider()?.removeAttribute("aria-busy");
      if (slider?.isConnected) slider.removeAttribute("data-sliding");
    });
};

const initTestimonialAutoplay = (scope = document) => {
  getScopedElements(scope, ".athoor-testimonial-slider__track").forEach((track) => {
    if (track.dataset.autoplayReady === "true") return;
    track.dataset.autoplayReady = "true";
    initSliderTrack(track);

    const section = track.closest(".athoor-testimonial-slider");
    const cards = [...track.children];
    if (cards.length < 2) return;
    const pauseButton = section.querySelector("[data-testimonial-pause]");
    const controls = section.querySelector(".athoor-testimonial-slider__controls");
    const speed = Number(section.dataset.testimonialSpeed) || 10;
    let cardsHovered = false;
    let controlsHovered = false;
    let userPaused = false;
    let stepping = false;
    let stepTimer = 0;
    let frame = 0;
    let lastTime = 0;
    let offset = 0;
    const releaseStep = () => {
      if (!stepping) return;

      window.clearTimeout(stepTimer);
      track.removeEventListener("scrollend", releaseStep);
      stepping = false;
      offset = track.scrollLeft;
    };
    const holdForStep = () => {
      stepping = true;
      offset = track.scrollLeft;
      window.clearTimeout(stepTimer);
      track.removeEventListener("scrollend", releaseStep);
      track.addEventListener("scrollend", releaseStep);
      stepTimer = window.setTimeout(releaseStep, 900);
    };
    const layout = () => {
      track.querySelectorAll("[data-testimonial-copy]").forEach((copy) => copy.remove());
      track.classList.remove("is-crawling");
      if (isReducedMotion()) return;
      const distance = getSliderStep(track) * cards.length;
      if (!distance) return;
      const copies = Math.ceil(track.clientWidth / distance);
      for (let index = 0; index < copies; index += 1) {
        cards.forEach((card) => {
          const copy = card.cloneNode(true);
          copy.dataset.testimonialCopy = "true";
          copy.setAttribute("aria-hidden", "true");
          copy.setAttribute("inert", "");
          [copy, ...copy.querySelectorAll("[id], [data-shopify-editor-block]")].forEach((node) => {
            node.removeAttribute("id");
            node.removeAttribute("data-shopify-editor-block");
          });
          copy.classList.add("is-in-view");
          track.append(copy);
        });
      }
      track.classList.add("is-crawling");
      updateSliderControls(track);
    };
    const cleanup = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;
      window.clearTimeout(stepTimer);
      track.removeEventListener("scrollend", releaseStep);
      resize.disconnect();
      visibility.disconnect();
      motion.removeEventListener("change", layout);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    const tick = (now) => {
      if (!track.isConnected) {
        cleanup();
        return;
      }

      frame = window.requestAnimationFrame(tick);
      const rect = section.getBoundingClientRect();
      const visible = rect.bottom > 0 && rect.top < window.innerHeight;
      const focused = document.activeElement;
      const controlsFocused =
        focused && focused !== pauseButton && controls?.contains(focused) && focused.matches(":focus-visible");
      const paused =
        !visible ||
        document.hidden ||
        cardsHovered ||
        controlsHovered ||
        controlsFocused ||
        userPaused ||
        stepping ||
        track.dataset.dragging === "true" ||
        isReducedMotion() ||
        !track.classList.contains("is-crawling");

      if (paused) {
        offset = track.scrollLeft;
        lastTime = 0;
        return;
      }

      const delta = lastTime ? Math.min(now - lastTime, 64) : 0;
      lastTime = now;
      if (!delta) return;

      const distance = getSliderStep(track) * cards.length;
      if (!distance) return;
      offset += (speed * delta) / 1000;
      offset %= distance;
      track.scrollLeft = offset;
    };
    const start = () => {
      if (frame || isReducedMotion()) return;
      lastTime = 0;
      frame = window.requestAnimationFrame(tick);
    };
    const onVisibility = () => {
      if (document.hidden) return;
      start();
    };
    const watchHover = (target, setHovered) => {
      if (!target) return;

      target.addEventListener("pointerenter", (event) => {
        if (event.pointerType === "mouse" || event.pointerType === "pen") setHovered(true);
      });
      target.addEventListener("pointerleave", () => {
        setHovered(false);
      });
    };
    watchHover(track, (hovered) => {
      cardsHovered = hovered;
    });
    watchHover(controls, (hovered) => {
      controlsHovered = hovered;
    });
    controls?.addEventListener("pointerdown", (event) => {
      if (event.target.closest("[data-scroll-target]")) holdForStep();
    });
    controls?.addEventListener(
      "click",
      (event) => {
        const control = event.target.closest("[data-scroll-target]");

        if (!control || control.disabled) return;

        event.stopPropagation();
        holdForStep();
        scrollSliderByDirection(control);
      },
      true,
    );
    pauseButton?.addEventListener("click", () => {
      userPaused = !userPaused;
      pauseButton.textContent = userPaused ? "Play testimonials" : "Pause testimonials";
      pauseButton.setAttribute("aria-pressed", String(userPaused));
    });
    const visibility = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) start();
    });
    visibility.observe(section);
    const resize = new ResizeObserver(layout);
    resize.observe(track);
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    motion.addEventListener("change", () => {
      layout();
      start();
    });
    document.addEventListener("visibilitychange", onVisibility);
    layout();
    start();
  });
};

const initLogoMarquee = (scope = document) => {
  getScopedElements(scope, "[data-logo-marquee]").forEach((marquee) => {
    if (marquee.dataset.logoMarqueeReady === "true") return;
    marquee.dataset.logoMarqueeReady = "true";

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const rows = [...marquee.querySelectorAll(".athoor-logo-marquee__viewport")];
    const layout = () => {
      rows.forEach((viewport) => {
        const track = viewport.querySelector("[data-logo-marquee-track]");
        const original = track?.querySelector("[data-logo-marquee-set]");
        if (!original) return;

        track.querySelectorAll("[data-logo-marquee-copy]").forEach((copy) => copy.remove());
        viewport.classList.remove("is-marquee-ready");
        track.style.removeProperty("--athoor-logo-distance");
        track.style.removeProperty("--athoor-logo-duration");

        const setWidth = original.getBoundingClientRect().width;
        const visibleWidth = viewport.getBoundingClientRect().width;
        if (reducedMotion.matches || !setWidth || !visibleWidth) return;

        // Cover the viewport plus one full set throughout the animation loop.
        const copyCount = Math.ceil(visibleWidth / setWidth);
        for (let index = 0; index < copyCount; index += 1) {
          const copy = original.cloneNode(true);
          copy.dataset.logoMarqueeCopy = "";
          copy.setAttribute("aria-hidden", "true");
          copy.querySelectorAll("[id]").forEach((element) => element.removeAttribute("id"));
          copy
            .querySelectorAll("[data-shopify-editor-block]")
            .forEach((element) => element.removeAttribute("data-shopify-editor-block"));
          copy.querySelectorAll("img").forEach((image) => {
            image.alt = "";
            image.loading = "eager";
          });
          track.append(copy);
        }
        track.style.setProperty("--athoor-logo-distance", `${setWidth}px`);
        track.style.setProperty("--athoor-logo-duration", `${(setWidth / 27).toFixed(2)}s`);
        viewport.classList.add("is-marquee-ready");
      });
    };

    let pendingLayout = 0;
    const scheduleLayout = () => {
      if (pendingLayout) return;
      pendingLayout = window.requestAnimationFrame(() => {
        pendingLayout = 0;
        layout();
      });
    };

    const observer = new ResizeObserver(scheduleLayout);
    rows.forEach((viewport) => {
      observer.observe(viewport);
      const original = viewport.querySelector("[data-logo-marquee-set]");
      if (original) observer.observe(original);
    });
    reducedMotion.addEventListener?.("change", scheduleLayout);
    document.fonts?.ready.then(scheduleLayout);
    window.addEventListener("load", scheduleLayout, { once: true });
    scheduleLayout();
  });
};

const runAfterHeroPaint = (callback) => {
  const run = () => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(callback, { timeout: 1200 });
      return;
    }

    window.setTimeout(callback, 1);
  };

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(run);
  });
};

runAfterHeroPaint(() => {
  initSliderTracks();
  initDragScrollTracks();
  updateHorizontalPins();
  initTrustIntroHovers();
  initShowreel();
  initEventGallery();
  initSignatureHeroSliders();
  initProductMarquees();
  initStoryScrolls();
  initProductBuyDialogs();
  initLogoMarquee();
  initTestimonialAutoplay();
  initProductCardReveal();
  initCollectionInfiniteLoading();
  initByTheNumbers();
  initPremiumScrollReveals();
  initPremiumParallax();
  updateStickyCollectionFilters();
  updatePartnerReveal();
  updateEventStacks();
});
const mobileStoryMotion = window.matchMedia("(max-width: 640px)");
mobileStoryMotion.addEventListener?.("change", () => {
  if (mobileStoryMotion.matches) {
    initPremiumScrollReveals();
    return;
  }

  document.querySelectorAll('.athoor-story-slider__slide[data-athoor-reveal="ready"]').forEach(revealPremiumElement);
});
const getTranslateX = (element) => {
  const value = window.getComputedStyle(element).transform;

  if (!value || value === "none") return 0;

  return new DOMMatrixReadOnly(value).m41;
};

const setTrackTranslateX = (track, x, animate = false) => {
  if (!animate || isReducedMotion()) {
    track.style.transition = "none";
    track.style.transform = `translate3d(${x}px, 0, 0)`;
    track.getBoundingClientRect();
    track.style.removeProperty("transition");
    return;
  }

  track.style.transform = `translate3d(${x}px, 0, 0)`;
};

const slideTitleSlider = (slider, direction) => {
  if (!slider || (direction !== "next" && direction !== "prev") || isReducedMotion()) {
    return Promise.resolve();
  }

  const track = slider.querySelector(".athoor-collection-slider__track, .athoor-event-slider__track");
  const active = slider.querySelector(
    ".athoor-collection-slider__item.is-active, .athoor-event-slider__item.is-active",
  );
  const items = [...slider.querySelectorAll(".athoor-collection-slider__item, .athoor-event-slider__item")];

  if (!track || !active) return Promise.resolve();

  const index = items.indexOf(active);
  const target = items[direction === "next" ? index + 1 : index - 1];

  if (!target) return Promise.resolve();

  const nextX = getTranslateX(track) + (active.offsetLeft - target.offsetLeft);

  active.classList.remove("is-active");
  target.classList.add("is-active");

  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      track.removeEventListener("transitionend", onEnd);
      resolve();
    };
    const onEnd = (event) => {
      if (event.target !== track || event.propertyName !== "transform") return;
      finish();
    };

    track.addEventListener("transitionend", onEnd);
    window.setTimeout(finish, 720);
    window.requestAnimationFrame(() => setTrackTranslateX(track, nextX, true));
  });
};

const positionEventSlider = (section) => {
  const track = section.querySelector(".athoor-event-slider__track, .athoor-collection-slider__track");
  const active = section.querySelector(
    ".athoor-event-slider__item.is-active, .athoor-collection-slider__item.is-active",
  );
  const viewport = section.querySelector(".athoor-event-slider__viewport, .athoor-collection-slider__viewport");
  const bar = section.querySelector(".athoor-event-slider__bar, .athoor-collection-slider__bar");
  const prev = track && track.querySelector(".athoor-event-slider__item, .athoor-collection-slider__item");

  if (!track || !active || !viewport) return;

  const isMobile = window.matchMedia("(max-width: 640px)").matches;
  const isTablet = window.matchMedia("(max-width: 989px)").matches;
  let x = 0;

  if (isMobile) {
    x = 20 - active.offsetLeft;
  } else if (isTablet) {
    x = (viewport.clientWidth - active.offsetWidth) / 2 - active.offsetLeft;
  } else if (prev) {
    x = prev.offsetWidth * -0.42;
  }

  setTrackTranslateX(track, x);

  if (!bar) return;

  const sectionRect = section.getBoundingClientRect();
  const activeRect = active.getBoundingClientRect();

  if (section.matches("[data-collection-slider]")) {
    const trackHeight = track.getBoundingClientRect().height;
    const unusedTitleHeight = Math.max(trackHeight - activeRect.height, 0);
    const styles = window.getComputedStyle(section);
    const desiredGap = Number.parseFloat(styles.getPropertyValue("--athoor-collection-title-description-gap")) || 48;
    bar.style.marginTop = `${desiredGap - unusedTitleHeight}px`;
  }

  bar.style.marginLeft = `${Math.max(activeRect.left - sectionRect.left, 0)}px`;
  bar.style.width = `${activeRect.width}px`;
};

const initEventSliders = (root = document) => {
  const scope = root.querySelectorAll ? root : document;
  const sections = [];

  if (scope.matches && scope.matches("[data-event-slider], [data-collection-slider]")) {
    sections.push(scope);
  }

  if (scope.querySelectorAll) {
    scope.querySelectorAll("[data-event-slider], [data-collection-slider]").forEach((section) => {
      sections.push(section);
    });
  }

  sections.forEach((section) => positionEventSlider(section));
};

runAfterHeroPaint(() => initEventSliders());

if (document.fonts && document.fonts.ready) {
  document.fonts.ready.then(() => initEventSliders());
}
window.addEventListener("resize", () => {
  document.querySelectorAll("[data-slider-track]").forEach((track) => {
    snapSliderToNearest(track, "auto");
    updateSliderControls(track);
  });
  scheduleHorizontalPinUpdate();
  scheduleStickyCollectionFilterUpdate();
  syncCollectionFilterModal();
  schedulePartnerRevealUpdate();
  scheduleEventStackUpdate();
  initEventSliders();
  scheduleCollectionGradientUpdate();
  schedulePremiumRevealUpdate();
  schedulePremiumParallaxUpdate();
});
const updateHeaderScrollState = (scrollYOverride) => {
  const scrollY = typeof scrollYOverride === "number" ? scrollYOverride : (mobileModalPageLock?.y ?? window.scrollY);
  const scrolled = scrollY > 8 ? "true" : "false";

  document.querySelectorAll("[data-header]").forEach((header) => {
    header.dataset.scrolled = scrolled;
  });
};

updateHeaderScrollState();
scheduleCollectionGradientUpdate();
window.addEventListener("scroll", updateHeaderScrollState, { passive: true });
window.addEventListener("scroll", scheduleHorizontalPinUpdate, { passive: true });
window.addEventListener("scroll", scheduleStickyCollectionFilterUpdate, { passive: true });
window.addEventListener("scroll", schedulePartnerRevealUpdate, { passive: true });
window.addEventListener("scroll", scheduleEventStackUpdate, { passive: true });
window.addEventListener("scroll", scheduleCollectionGradientUpdate, { passive: true });
window.addEventListener("scroll", schedulePremiumRevealUpdate, { passive: true });
window.addEventListener("scroll", schedulePremiumParallaxUpdate, { passive: true });
document.addEventListener("shopify:section:load", updateHeaderScrollState);
document.addEventListener("shopify:section:load", initSliderTracks);
document.addEventListener("shopify:section:load", initDragScrollTracks);
document.addEventListener("shopify:section:load", scheduleHorizontalPinUpdate);
document.addEventListener("shopify:section:load", initTrustIntroHovers);
document.addEventListener("shopify:section:load", (event) => initShowreel(event.target));
document.addEventListener("shopify:section:load", (event) => initEventGallery(event.target));
document.addEventListener("shopify:section:unload", (event) => {
  const gallery = event.target.matches?.("[data-event-gallery]")
    ? event.target
    : event.target.querySelector?.("[data-event-gallery]");

  if (gallery?.dataset.projectOpen === "true") closeEventProjectGallery(gallery, false);
});
document.addEventListener("shopify:section:load", (event) => initSignatureHeroSliders(event.target));
document.addEventListener("shopify:section:load", initProductMarquees);
document.addEventListener("shopify:section:load", (event) => initStoryScrolls(event.target));
document.addEventListener("shopify:section:unload", (event) => stopStoryScroll(event.target));
document.addEventListener("shopify:section:load", (event) => initLogoMarquee(event.target));
document.addEventListener("shopify:section:load", (event) => initProductBuyDialogs(event.target));
document.addEventListener("shopify:section:load", scheduleStickyCollectionFilterUpdate);
document.addEventListener("shopify:section:load", schedulePartnerRevealUpdate);
document.addEventListener("shopify:section:load", scheduleEventStackUpdate);
document.addEventListener("shopify:section:load", (event) => initEventSliders(event.target));
document.addEventListener("shopify:section:load", (event) => initProductCardReveal(event.target));
document.addEventListener("shopify:section:load", (event) => initCollectionInfiniteLoading(event.target));
document.addEventListener("shopify:section:load", (event) => initByTheNumbers(event.target));
document.addEventListener("shopify:section:load", (event) => initPremiumScrollReveals(event.target));
document.addEventListener("shopify:section:load", (event) => initPremiumParallax(event.target));

const PREDICTIVE_SEARCH_DEBOUNCE = 280;
const PREDICTIVE_SEARCH_MIN_LENGTH = 2;

let predictiveSearchTimer = 0;
let predictiveSearchAbort = null;

const getHeaderSearchInput = (header) => header?.querySelector("[data-header-search-input]");

const getHeaderSearchLive = (header) => header?.querySelector("[data-predictive-search-live]");

const getHeaderSearchIdle = (header) => header?.querySelector("[data-predictive-search-idle]");

const cancelPredictiveSearch = () => {
  window.clearTimeout(predictiveSearchTimer);
  predictiveSearchAbort?.abort();
  predictiveSearchAbort = null;
};

const updateHeaderSearchFilled = (header, value) => {
  if (!header) return;

  const hasValue = Boolean(value.trim());
  const clearButton = header.querySelector("[data-header-search-clear]");

  header.dataset.searchFilled = String(hasValue);

  if (clearButton) clearButton.hidden = !hasValue;
};

const resetPredictiveSearchResults = (header) => {
  const idle = getHeaderSearchIdle(header);
  const live = getHeaderSearchLive(header);
  const filled = header.dataset.searchFilled === "true";

  if (idle) idle.hidden = filled;
  if (live) {
    live.hidden = true;
    live.innerHTML = "";
    live.removeAttribute("aria-busy");
    delete live.dataset.resultsRevealed;
  }
};

const showPredictiveSearchResults = (header, html) => {
  const idle = getHeaderSearchIdle(header);
  const live = getHeaderSearchLive(header);

  if (!live) return;

  const container = document.createElement("div");
  container.innerHTML = html;
  const markup = container.querySelector("[data-predictive-search-markup]");
  const markupHtml = markup?.innerHTML.trim() || "";

  live.removeAttribute("aria-busy");

  if (!markupHtml) {
    live.innerHTML = "";
    live.hidden = true;
    if (idle) idle.hidden = header.dataset.searchFilled === "true";
    return;
  }

  live.dataset.resultsRevealed = "false";
  live.innerHTML = markupHtml;
  live.hidden = false;
  if (idle) idle.hidden = true;

  if (isReducedMotion()) {
    live.dataset.resultsRevealed = "true";
    return;
  }

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      live.dataset.resultsRevealed = "true";
    });
  });
};

const buildPredictiveSearchUrl = (header, query) => {
  const endpoint = header?.querySelector("[data-predictive-search-url]")?.dataset.predictiveSearchUrl;

  if (!endpoint) return null;

  const url = new URL(endpoint, window.location.origin);
  url.searchParams.set("q", query);
  url.searchParams.set("resources[type]", "product,query,collection");
  url.searchParams.set("resources[limit]", "10");
  url.searchParams.set("resources[limit_scope]", "each");
  url.searchParams.set("resources[unavailable_products]", "hide");
  url.searchParams.set("section_id", "predictive-search");

  return url;
};

const requestPredictiveSearch = (header, query) => {
  const url = buildPredictiveSearchUrl(header, query);
  const live = getHeaderSearchLive(header);

  if (!url || !live) return;

  predictiveSearchAbort?.abort();
  predictiveSearchAbort = new AbortController();
  live.setAttribute("aria-busy", "true");

  fetch(url, { signal: predictiveSearchAbort.signal })
    .then((response) => {
      if (!response.ok) throw new Error("Predictive search request failed");
      return response.text();
    })
    .then((html) => {
      if (header.dataset.searchOpen !== "true") return;
      showPredictiveSearchResults(header, html);
    })
    .catch((error) => {
      if (error.name === "AbortError") return;
      live.removeAttribute("aria-busy");
    });
};

const schedulePredictiveSearch = (header, value) => {
  const query = value.trim();

  updateHeaderSearchFilled(header, value);
  cancelPredictiveSearch();

  if (!query) {
    resetPredictiveSearchResults(header);
    return;
  }

  const idle = getHeaderSearchIdle(header);

  if (idle) idle.hidden = true;

  if (query.length < PREDICTIVE_SEARCH_MIN_LENGTH) {
    const live = getHeaderSearchLive(header);

    if (live) {
      live.hidden = true;
      live.innerHTML = "";
      live.removeAttribute("aria-busy");
    }

    return;
  }

  predictiveSearchTimer = window.setTimeout(() => {
    requestPredictiveSearch(header, query);
  }, PREDICTIVE_SEARCH_DEBOUNCE);
};

const MENU_SLIDE_MS = 420;
const SEARCH_SLIDE_MS = 420;
const SEARCH_CLOSE_GUARD_MS = 400;
const HEADER_MENU_MODAL_MQ = "(max-width: 989px)";
let menuCloseTimer = 0;
let menuItemsTimer = 0;
let searchCloseTimer = 0;
let searchCloseGuardUntil = 0;

const isHeaderMenuModalViewport = () => window.matchMedia(HEADER_MENU_MODAL_MQ).matches;

const isHeaderSearchSlideViewport = () => isHeaderMenuModalViewport();

const setHeaderMenuPageInert = (open) => {
  document.getElementById("MainContent")?.toggleAttribute("inert", open);
  document.querySelector(".shopify-section-group-footer-group")?.toggleAttribute("inert", open);
};

const syncHeaderSearchModal = (header) => {
  if (!header) return;

  const panel = header.querySelector("[data-header-search]");
  const backdrop = header.querySelector(".athoor-modal--search .athoor-modal__backdrop");
  const isOpen = header.dataset.searchOpen === "true";

  if (!panel) return;

  panel.toggleAttribute("inert", !isOpen);
  backdrop?.toggleAttribute("inert", !isOpen);

  if (isOpen) {
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-modal", "true");
    panel.setAttribute("aria-hidden", "false");
  } else {
    panel.removeAttribute("role");
    panel.removeAttribute("aria-modal");
    panel.setAttribute("aria-hidden", "true");
  }
};

const syncHeaderOverlayInert = () => {
  const menuOpen = Boolean(document.querySelector('[data-header][data-menu-open="true"]'));
  const searchOpen = Boolean(document.querySelector('[data-header][data-search-open="true"]'));

  setHeaderMenuPageInert((menuOpen && isHeaderMenuModalViewport()) || searchOpen);
};

const syncHeaderMenuModal = (header) => {
  if (!header) return;

  const panel = header.querySelector("[data-header-menu]");
  const backdrop = header.querySelector("[data-header-menu-close]");
  const useModal = isHeaderMenuModalViewport();
  const isOpen = header.dataset.menuOpen === "true";

  if (!panel) return;

  panel.toggleAttribute("inert", useModal && !isOpen);
  backdrop?.toggleAttribute("inert", !useModal || !isOpen);

  if (useModal && isOpen) {
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-modal", "true");
    panel.setAttribute("aria-label", "Main menu");
    panel.setAttribute("aria-hidden", "false");
  } else {
    panel.removeAttribute("role");
    panel.removeAttribute("aria-modal");
    panel.removeAttribute("aria-label");
    panel.setAttribute("aria-hidden", useModal ? "true" : "false");
  }

  placeHeaderMega(header);
  syncHeaderMega(header);
};

const syncAllHeaderMenus = () => {
  document.querySelectorAll("[data-header]").forEach((header) => {
    syncHeaderMenuModal(header);
    syncHeaderSearchModal(header);
  });
  syncHeaderOverlayInert();
};

window.matchMedia(HEADER_MENU_MODAL_MQ).addEventListener("change", syncAllHeaderMenus);
window.addEventListener("resize", syncAllHeaderMenus);
document.addEventListener("shopify:section:load", syncAllHeaderMenus);

const finishCloseHeaderSearch = (header, restoreFocus = false) => {
  window.clearTimeout(searchCloseTimer);
  if (!header) return;

  header.dataset.searchOpen = "false";
  header.dataset.searchRevealed = "false";
  header.querySelectorAll("[data-header-search-toggle]").forEach((toggle) => {
    toggle.setAttribute("aria-expanded", "false");
  });
  header.querySelector("[data-header-search]")?.setAttribute("aria-hidden", "true");
  document.body.classList.remove("athoor-search-open");
  cancelPredictiveSearch();
  syncHeaderSearchModal(header);
  syncHeaderOverlayInert();
  searchCloseGuardUntil = Date.now() + SEARCH_CLOSE_GUARD_MS;

  if (restoreFocus) {
    header.querySelector("[data-header-search-toggle]")?.focus({ preventScroll: true });
  }
};

const closeHeaderSearch = (header, restoreFocus = false) => {
  if (!header || header.dataset.searchOpen !== "true") return;

  if (isReducedMotion() || header.dataset.searchRevealed !== "true") {
    finishCloseHeaderSearch(header, restoreFocus);
    return;
  }

  header.dataset.searchRevealed = "false";
  window.clearTimeout(searchCloseTimer);
  searchCloseTimer = window.setTimeout(() => finishCloseHeaderSearch(header, restoreFocus), SEARCH_SLIDE_MS);
};

const syncHeaderMenuLabels = (header) => {
  const isOpen = header.dataset.menuOpen === "true";
  const closed = header.querySelector("[data-menu-label-closed]");
  const opened = header.querySelector("[data-menu-label-open]");

  if (closed) closed.hidden = isOpen;
  if (opened) opened.hidden = !isOpen;
};

const finishCloseHeaderMenu = (header) => {
  window.clearTimeout(menuCloseTimer);
  window.clearTimeout(menuItemsTimer);
  header.dataset.menuOpen = "false";
  header.dataset.menuRevealed = "false";
  header.dataset.menuItems = "false";
  header.dataset.megaOpen = "false";
  header.querySelector("[data-header-menu-toggle]")?.setAttribute("aria-expanded", "false");
  syncHeaderMenuLabels(header);
  document.body.classList.toggle(
    "athoor-menu-open",
    Boolean(document.querySelector('[data-header][data-menu-open="true"]')),
  );
  syncAllHeaderMenus();
};

const MEGA_CLOSE_MS = 140;
let megaCloseTimer = 0;

const isHeaderMegaTarget = (node) => Boolean(node?.closest?.("[data-header-mega-trigger], [data-header-mega]"));

const placeHeaderMega = (header) => {
  const mega = header?.querySelector("[data-header-mega]");
  const panel = header?.querySelector("[data-header-menu]");

  if (!mega || !panel) return;

  if (isHeaderMenuModalViewport()) {
    panel.append(mega);
    return;
  }

  header.append(mega);
};

const syncMegaAccordions = (header, collapseMobile = false) => {
  if (!header) return;

  const mobile = isHeaderMenuModalViewport();

  header.querySelectorAll("[data-header-mega-group]").forEach((group) => {
    if (!(group instanceof HTMLDetailsElement)) return;

    if (mobile) {
      if (collapseMobile) group.open = false;
      return;
    }

    group.open = true;
  });
};

const activateHeaderMegaPanel = (parentLink) => {
  const directory = parentLink?.closest?.("[data-header-mega-directory]");
  const panelKey = parentLink?.dataset?.headerMegaParent;

  if (!directory || panelKey === undefined) return;

  directory.querySelectorAll("[data-header-mega-parent]").forEach((link) => {
    const active = link === parentLink;
    link.classList.toggle("is-active", active);
    link.setAttribute("aria-expanded", String(active));
  });

  directory.querySelectorAll("[data-header-mega-panel]").forEach((panel) => {
    panel.hidden = panel.dataset.headerMegaPanel !== panelKey;
  });
};

const syncHeaderMega = (header) => {
  if (!header) return;

  const mega = header.querySelector("[data-header-mega]");
  const trigger = header.querySelector("[data-header-mega-trigger] > a");
  const open = header.dataset.megaOpen === "true";
  const mobile = isHeaderMenuModalViewport();

  if (!mega) return;

  if (trigger) trigger.setAttribute("aria-expanded", String(open));
  mega.toggleAttribute("inert", mobile && (header.dataset.menuOpen !== "true" || !open));
  header.querySelector("[data-header-menu] .athoor-header__menu")?.toggleAttribute("inert", mobile && open);
  header.querySelector(".athoor-header__menu-meta")?.toggleAttribute("inert", mobile && open);
  if (!mobile) syncMegaAccordions(header);
};

const closeHeaderMega = (header) => {
  if (!header) return;

  window.clearTimeout(megaCloseTimer);
  header.dataset.megaOpen = "false";
  syncMegaAccordions(header, true);
  syncHeaderMega(header);
};

const openHeaderMega = (header) => {
  if (!header || isHeaderMenuModalViewport()) return;

  window.clearTimeout(megaCloseTimer);
  finishCloseHeaderSearch(header);
  header.dataset.megaOpen = "true";
  syncHeaderMega(header);
};

const scheduleCloseHeaderMega = (header) => {
  if (!header || isHeaderMenuModalViewport()) return;

  window.clearTimeout(megaCloseTimer);
  megaCloseTimer = window.setTimeout(() => closeHeaderMega(header), MEGA_CLOSE_MS);
};

const initHeaderMega = (header) => {
  if (!header || header.dataset.megaReady === "true") return;

  const mega = header.querySelector("[data-header-mega]");
  const trigger = header.querySelector("[data-header-mega-trigger]");

  if (!mega || !trigger) return;

  header.dataset.megaReady = "true";
  placeHeaderMega(header);
  syncMegaAccordions(header, isHeaderMenuModalViewport());
  syncHeaderMega(header);

  header.addEventListener(
    "toggle",
    (event) => {
      const group = event.target;

      if (!(group instanceof HTMLDetailsElement) || !group.hasAttribute("data-header-mega-group")) return;
      if (!isHeaderMenuModalViewport() && !group.open) group.open = true;
    },
    true,
  );

  header.addEventListener("pointerover", (event) => {
    const parentLink = event.target.closest?.("[data-header-mega-parent]");
    if (parentLink && header.contains(parentLink)) activateHeaderMegaPanel(parentLink);
    if (isHeaderMegaTarget(event.target)) openHeaderMega(header);
  });

  header.addEventListener("pointerout", (event) => {
    if (!isHeaderMegaTarget(event.target)) return;
    if (isHeaderMegaTarget(event.relatedTarget)) return;
    scheduleCloseHeaderMega(header);
  });

  header.addEventListener("focusin", (event) => {
    const parentLink = event.target.closest?.("[data-header-mega-parent]");
    if (parentLink && header.contains(parentLink)) activateHeaderMegaPanel(parentLink);
    if (isHeaderMegaTarget(event.target)) openHeaderMega(header);
  });

  header.addEventListener("focusout", (event) => {
    if (event.relatedTarget && header.contains(event.relatedTarget) && isHeaderMegaTarget(event.relatedTarget)) {
      return;
    }
    scheduleCloseHeaderMega(header);
  });

  header.addEventListener("click", (event) => {
    if (!isHeaderMenuModalViewport()) return;

    const back = event.target.closest("[data-header-mega-back]");

    if (back && header.contains(back)) {
      event.preventDefault();
      closeHeaderMega(header);
      header.querySelector("[data-header-mega-trigger] > a")?.focus({ preventScroll: true });
      return;
    }

    const groupLink = event.target.closest("[data-header-mega-group] .athoor-faq__question");

    if (groupLink && header.contains(groupLink)) {
      const group = groupLink.closest("[data-header-mega-group]");

      event.preventDefault();
      event.stopPropagation();
      if (group instanceof HTMLDetailsElement) group.open = !group.open;
      return;
    }

    const link = event.target.closest("[data-header-mega-trigger] > a");

    if (!link || !header.contains(link)) return;

    event.preventDefault();

    if (header.dataset.megaOpen === "true") closeHeaderMega(header);
    else {
      header.dataset.megaOpen = "true";
      syncMegaAccordions(header, true);
      syncHeaderMega(header);
      header.querySelector("[data-header-mega-back]")?.focus({ preventScroll: true });
    }
  });
};

const setHeaderContactOpen = (header, open, restoreFocus = false) => {
  const toggle = header.querySelector("[data-header-contact-toggle]");
  const menu = header.querySelector("[data-header-contact-menu]");

  if (!toggle || !menu) return;

  const wasOpen = !menu.hidden;

  menu.hidden = !open;
  toggle.setAttribute("aria-expanded", String(open));

  if (!open && restoreFocus && wasOpen) toggle.focus({ preventScroll: true });
};

const initHeaderContact = (header) => {
  const root = header.querySelector("[data-header-contact]");
  const toggle = root?.querySelector("[data-header-contact-toggle]");
  const menu = root?.querySelector("[data-header-contact-menu]");

  if (!root || !toggle || !menu || root.dataset.contactReady === "true") return;

  root.dataset.contactReady = "true";

  toggle.addEventListener("click", () => {
    const open = menu.hidden;

    document.querySelectorAll("[data-header]").forEach((item) => setHeaderContactOpen(item, false));
    if (open) setHeaderContactOpen(header, true);
  });

  toggle.addEventListener("keydown", (event) => {
    if (event.key !== "ArrowDown" || !menu.hidden) return;

    event.preventDefault();
    setHeaderContactOpen(header, true);
    menu.querySelector("a")?.focus({ preventScroll: true });
  });

  menu.addEventListener("keydown", (event) => {
    const links = [...menu.querySelectorAll("a")];
    const index = links.indexOf(document.activeElement);

    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;

    event.preventDefault();
    const next = event.key === "ArrowDown" ? index + 1 : index - 1;
    links[(next + links.length) % links.length]?.focus({ preventScroll: true });
  });
};

document.addEventListener("click", (event) => {
  document.querySelectorAll("[data-header-contact]").forEach((root) => {
    if (root.contains(event.target)) return;

    const header = root.closest("[data-header]");
    if (header) setHeaderContactOpen(header, false);
  });
});

document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape") return;

  document.querySelectorAll("[data-header]").forEach((header) => setHeaderContactOpen(header, false, true));
});

syncAllHeaderMenus();
document.querySelectorAll("[data-header]").forEach((header) => {
  initHeaderMega(header);
  initHeaderContact(header);
});
document.addEventListener("shopify:section:load", () => {
  document.querySelectorAll("[data-header]").forEach((header) => {
    initHeaderMega(header);
    initHeaderContact(header);
  });
});
window.matchMedia(HEADER_MENU_MODAL_MQ).addEventListener("change", () => {
  document.querySelectorAll("[data-header]").forEach((header) => {
    closeHeaderMega(header);
    syncHeaderMega(header);
  });
});

const openHeaderMenu = (header) => {
  if (!header) return;

  window.clearTimeout(menuCloseTimer);
  window.clearTimeout(menuItemsTimer);
  finishCloseHeaderSearch(header);
  closeHeaderMega(header);
  header.dataset.menuOpen = "true";
  header.dataset.menuRevealed = "false";
  header.dataset.menuItems = "false";
  header.querySelector("[data-header-menu-toggle]")?.setAttribute("aria-expanded", "true");
  syncHeaderMenuLabels(header);
  document.body.classList.add("athoor-menu-open");
  syncAllHeaderMenus();

  if (isReducedMotion()) {
    header.dataset.menuRevealed = "true";
    header.dataset.menuItems = "true";
    return;
  }

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      header.dataset.menuRevealed = "true";
      menuItemsTimer = window.setTimeout(() => {
        header.dataset.menuItems = "true";
      }, MENU_SLIDE_MS);
    });
  });
};

const closeHeaderMenu = (header) => {
  if (!header || header.dataset.menuOpen !== "true") return;

  window.clearTimeout(menuItemsTimer);
  header.dataset.menuItems = "false";

  if (isReducedMotion() || header.dataset.menuRevealed !== "true") {
    finishCloseHeaderMenu(header);
    return;
  }

  header.dataset.menuRevealed = "false";
  window.clearTimeout(menuCloseTimer);
  menuCloseTimer = window.setTimeout(() => finishCloseHeaderMenu(header), MENU_SLIDE_MS);
};

let collectionFilterOpen = false;

const getCollectionFilterForm = () => document.querySelector("[data-collection-filter-form]");

const syncCollectionFilterModal = ({ open = collectionFilterOpen, moveFocus = false, restoreFocus = false } = {}) => {
  const form = getCollectionFilterForm();
  const sidebar = form?.querySelector("[data-sticky-filter-sidebar]");
  const backdrop = form?.querySelector(".athoor-collection-filters__backdrop");
  const openButton = form?.querySelector("[data-filter-modal-open]");
  const closeButton = form?.querySelector(".athoor-collection-filters__close");
  const isOpen = Boolean(form && sidebar && open);

  collectionFilterOpen = isOpen;

  if (!form || !sidebar) {
    document.body.classList.remove("athoor-filter-modal-open");
    return;
  }

  form.dataset.filterOpen = isOpen ? "open" : "closed";
  form.dataset.filterModal = isOpen ? "open" : "closed";
  sidebar.toggleAttribute("inert", !isOpen);
  backdrop?.toggleAttribute("inert", !isOpen);
  openButton?.setAttribute("aria-expanded", String(isOpen));
  sidebar.setAttribute("aria-modal", isOpen ? "true" : "false");

  if (isOpen) {
    sidebar.setAttribute("role", "dialog");
  } else {
    sidebar.removeAttribute("role");
  }

  document.body.classList.toggle("athoor-filter-modal-open", isOpen);

  if (isOpen) {
    document.querySelectorAll('[data-header][data-menu-open="true"]').forEach(closeHeaderMenu);
    document
      .querySelectorAll('[data-header][data-search-open="true"]')
      .forEach((header) => finishCloseHeaderSearch(header));

    if (moveFocus) closeButton?.focus({ preventScroll: true });
  } else if (restoreFocus) {
    openButton?.focus({ preventScroll: true });
  }
};

const FILTER_ACCORDION_MS = 360;
const filterAccordionTimers = new WeakMap();

const toggleCollectionFilterGroup = (group) => {
  if (!(group instanceof HTMLDetailsElement)) return;

  const activeTimer = filterAccordionTimers.get(group);

  if (activeTimer) {
    window.clearTimeout(activeTimer);
    filterAccordionTimers.delete(group);
  }

  if (isReducedMotion()) {
    group.open = !group.open;
    delete group.dataset.filterCollapsing;
    return;
  }

  if (group.dataset.filterCollapsing === "true") {
    delete group.dataset.filterCollapsing;
    return;
  }

  if (group.open) {
    group.dataset.filterCollapsing = "true";
    const timer = window.setTimeout(() => {
      group.open = false;
      delete group.dataset.filterCollapsing;
      filterAccordionTimers.delete(group);
    }, FILTER_ACCORDION_MS);
    filterAccordionTimers.set(group, timer);
    return;
  }

  group.dataset.filterCollapsing = "true";
  group.open = true;
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      delete group.dataset.filterCollapsing;
    });
  });
};

const setCollectionFilterModalOpen = (open, options = {}) => {
  collectionFilterOpen = Boolean(open);
  syncCollectionFilterModal({ open: collectionFilterOpen, ...options });
};

syncCollectionFilterModal();
document.addEventListener("shopify:section:load", syncCollectionFilterModal);

const openHeaderSearch = (header) => {
  if (!header) return;

  const searchInput = getHeaderSearchInput(header);

  if (header.dataset.searchOpen === "true" && header.dataset.searchRevealed === "true") {
    searchInput?.focus();
    return;
  }

  window.clearTimeout(menuCloseTimer);
  window.clearTimeout(menuItemsTimer);
  window.clearTimeout(searchCloseTimer);
  closeHeaderMega(header);
  header.dataset.searchOpen = "true";
  header.dataset.searchRevealed = "false";
  header.dataset.menuOpen = "false";
  header.dataset.menuRevealed = "false";
  header.dataset.menuItems = "false";
  header.querySelector("[data-header-menu-toggle]")?.setAttribute("aria-expanded", "false");
  syncHeaderMenuLabels(header);
  header.querySelectorAll("[data-header-search-toggle]").forEach((toggle) => {
    toggle.setAttribute("aria-expanded", "true");
  });
  header.querySelector("[data-header-search]")?.setAttribute("aria-hidden", "false");
  document.body.classList.remove("athoor-menu-open");
  document.body.classList.add("athoor-search-open");
  updateHeaderSearchFilled(header, searchInput?.value || "");
  syncAllHeaderMenus();

  if (isReducedMotion() || !isHeaderSearchSlideViewport()) {
    header.dataset.searchRevealed = "true";
    searchInput?.focus();
    return;
  }

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      header.dataset.searchRevealed = "true";
      searchInput?.focus();
    });
  });
};

document.addEventListener("click", (event) => {
  const collectionLoadRetry = event.target.closest("[data-collection-load-retry]");

  if (collectionLoadRetry) {
    loadNextCollectionPage(collectionLoadRetry.closest("[data-collection-load]"));
    return;
  }

  const collectionBackToTop = event.target.closest("[data-collection-back-to-top]");

  if (collectionBackToTop) {
    event.preventDefault();
    const target = document.querySelector(collectionBackToTop.hash);
    target?.scrollIntoView({ behavior: isReducedMotion() ? "auto" : "smooth", block: "start" });
    return;
  }

  const filterSummary = event.target.closest(".athoor-collection-filters__summary");

  if (filterSummary) {
    const group = filterSummary.closest(".athoor-collection-filters__group");

    if (group) {
      event.preventDefault();
      toggleCollectionFilterGroup(group);
      return;
    }
  }

  const scrollControl = event.target.closest("[data-scroll-target]");

  if (scrollControl) {
    scrollSliderByDirection(scrollControl);
    return;
  }

  const collectionFilterLink = event.target.closest("[data-collection-filter-link]");

  if (collectionFilterLink) {
    if (!isPlainPrimaryNavigation(event, collectionFilterLink)) return;

    const url = new URL(collectionFilterLink.href, window.location.origin);

    if (url.origin !== window.location.origin) return;

    event.preventDefault();

    const sliderLink = collectionFilterLink.closest("[data-collection-slider]");
    const sliderArrow = collectionFilterLink.closest("[data-collection-slider] .athoor-slider-button");
    let sliderDirection = "";

    if (sliderArrow?.classList.contains("athoor-slider-button--left")) sliderDirection = "prev";
    if (sliderArrow?.classList.contains("athoor-slider-button--right")) sliderDirection = "next";

    renderCollectionSection(url, true, {
      scrollToProducts: collectionFilterLink.hasAttribute("data-scroll-to-products"),
      focusHeading: Boolean(sliderLink),
      sliderDirection,
    });
    return;
  }

  const eventSliderNav = event.target.closest("[data-event-slider-nav]");

  if (eventSliderNav) {
    if (!isPlainPrimaryNavigation(event, eventSliderNav)) return;

    const url = new URL(eventSliderNav.href, window.location.origin);

    if (url.origin !== window.location.origin) return;

    event.preventDefault();

    if (url.pathname === window.location.pathname && url.search === window.location.search) return;

    const sliderArrow = eventSliderNav.closest("[data-event-slider] .athoor-slider-button");
    let sliderDirection = "";

    if (sliderArrow?.classList.contains("athoor-slider-button--left")) sliderDirection = "prev";
    if (sliderArrow?.classList.contains("athoor-slider-button--right")) sliderDirection = "next";

    renderEventPage(url, true, { focusHeading: true, sliderDirection });
    return;
  }

  const filterModalOpen = event.target.closest("[data-filter-modal-open]");
  const filterModalClose = event.target.closest("[data-filter-modal-close]");

  if (filterModalOpen) {
    event.preventDefault();
    setCollectionFilterModalOpen(!collectionFilterOpen, {
      moveFocus: !collectionFilterOpen,
      restoreFocus: collectionFilterOpen,
    });
    return;
  }

  if (filterModalClose) {
    event.preventDefault();
    setCollectionFilterModalOpen(false, { restoreFocus: true });
    return;
  }

  document.querySelectorAll("[data-sort-menu][open]").forEach((menu) => {
    if (menu.contains(event.target)) return;
    menu.removeAttribute("open");
  });

  const searchToggle = event.target.closest("[data-header-search-toggle]");
  const searchClose = event.target.closest("[data-header-search-close]");
  const searchClear = event.target.closest("[data-header-search-clear]");
  const menuClose = event.target.closest("[data-header-menu-close]");
  const toggle = event.target.closest("[data-header-menu-toggle]");
  const header = event.target.closest("[data-header]");

  if (menuClose) {
    const closeHeader = menuClose.closest("[data-header]");
    if (closeHeader) closeHeaderMenu(closeHeader);
    return;
  }

  if (searchToggle && header) {
    if (header.dataset.searchOpen === "true" && header.dataset.searchRevealed === "true") {
      closeHeaderSearch(header, true);
    } else {
      openHeaderSearch(header);
    }

    return;
  }

  if (searchClear && header) {
    const searchInput = getHeaderSearchInput(header);

    if (searchInput) {
      searchInput.value = "";
      schedulePredictiveSearch(header, "");
      searchInput.focus();
    }

    return;
  }

  if (searchClose) {
    event.preventDefault();
    const closeHeader = searchClose.closest("[data-header]");
    if (closeHeader) closeHeaderSearch(closeHeader, true);
    return;
  }

  if (toggle && header) {
    if (Date.now() < searchCloseGuardUntil) return;

    if (header.dataset.menuOpen === "true" && header.dataset.menuRevealed === "true") {
      closeHeaderMenu(header);
    } else {
      openHeaderMenu(header);
    }

    return;
  }

  document.querySelectorAll('[data-header][data-menu-open="true"]').forEach((openHeader) => {
    if (openHeader.contains(event.target)) return;

    closeHeaderMenu(openHeader);
  });

  document.querySelectorAll('[data-header][data-search-open="true"]').forEach((openHeader) => {
    if (openHeader.contains(event.target)) return;

    closeHeaderSearch(openHeader);
  });

  document.body.classList.toggle(
    "athoor-menu-open",
    Boolean(document.querySelector('[data-header][data-menu-open="true"]')),
  );
});

document.addEventListener("input", (event) => {
  const searchInput = event.target.closest("[data-header-search-input]");

  if (!searchInput) return;

  const header = searchInput.closest("[data-header]");

  if (!header) return;

  if (header.dataset.searchOpen !== "true") openHeaderSearch(header);
  schedulePredictiveSearch(header, searchInput.value);
});

const contactFieldValue = (form, name) => {
  const field = form.elements.namedItem(name);

  if (!field || field instanceof RadioNodeList) return "";

  return String(field.value || "").trim();
};

const buildContactWhatsAppUrl = (form) => {
  const phone = form.dataset.contactWhatsapp;

  if (!phone) return "";

  const lines = ["Hi, I'm looking to make an enquiry. Here are the details:", ""];

  [
    ["contact[name]", "Name"],
    ["contact[email]", "Email"],
    ["contact[phone]", "Phone"],
    ["contact[Event type]", "Event type"],
    ["contact[Event date]", "Event date"],
    ["contact[Estimated guest count]", "Guests"],
    ["contact[body]", "Request"],
  ].forEach(([name, label]) => {
    let value = contactFieldValue(form, name);

    if (name === "contact[Event date]" && value) value = formatContactDateLabel(value) || value;

    if (value) lines.push(`${label}: ${value}`);
  });

  return `https://wa.me/${phone}?text=${encodeURIComponent(lines.join("\n"))}`;
};

const sendNewsletterContact = (customerForm) => {
  const contactForm = customerForm.closest("[data-newsletter]")?.querySelector("[data-newsletter-contact]");
  const email = customerForm.querySelector('input[name="contact[email]"]')?.value?.trim();

  if (!contactForm || !email) return Promise.resolve();

  const payload = new FormData(contactForm);
  payload.set("contact[email]", email);
  payload.set("contact[name]", "Newsletter");
  payload.set("contact[body]", `Newsletter signup: ${email}`);

  return fetch(contactForm.getAttribute("action") || "/contact", {
    method: "POST",
    body: payload,
    headers: { Accept: "application/json" },
  }).then(
    () => undefined,
    () => undefined,
  );
};

document.addEventListener("submit", (event) => {
  const filterForm = event.target.closest("[data-collection-filter-form]");

  if (filterForm) {
    event.preventDefault();
    renderCollectionSection(buildCollectionFilterUrl(filterForm));
    return;
  }

  const newsletterForm = event.target.closest("[data-newsletter-customer]");

  if (newsletterForm && newsletterForm.dataset.newsletterSent !== "true") {
    event.preventDefault();
    newsletterForm.dataset.newsletterSent = "true";
    const submitButton = newsletterForm.querySelector("[type='submit']");
    if (submitButton) submitButton.disabled = true;

    const finish = () => {
      if (newsletterForm.isConnected) newsletterForm.submit();
    };

    Promise.race([
      sendNewsletterContact(newsletterForm),
      new Promise((resolve) => window.setTimeout(resolve, 2500)),
    ]).then(finish, finish);
    return;
  }

  const contactForm = event.target.closest("[data-contact-form]");

  if (!contactForm || !contactForm.checkValidity()) return;

  const whatsappUrl = buildContactWhatsAppUrl(contactForm);

  if (whatsappUrl) window.open(whatsappUrl, "_blank", "noopener,noreferrer");
});

document.addEventListener("change", (event) => {
  const form = event.target.closest("[data-collection-filter-form]");

  if (form) {
    event.target.closest("[data-sort-menu]")?.removeAttribute("open");
    form.requestSubmit();
    return;
  }

  const native = event.target.closest("[data-contact-select-native]");
  const selectRoot = native?.closest("[data-contact-select]");

  if (!selectRoot) return;

  syncContactSelectLabel(selectRoot);
  syncContactSelectOther(selectRoot);
});

document.addEventListener("input", (event) => {
  const textarea = event.target.closest("[data-contact-autogrow]");

  if (!textarea) return;

  resizeContactTextarea(textarea);
});

const closeContactSelects = (exceptRoot) => {
  document.querySelectorAll("[data-contact-select][data-open='true']").forEach((root) => {
    if (root === exceptRoot) return;

    root.dataset.open = "false";
    root.querySelector("[data-contact-select-toggle]")?.setAttribute("aria-expanded", "false");
  });
};

const closeContactDatePickers = (exceptRoot) => {
  document.querySelectorAll("[data-contact-date][data-open='true']").forEach((root) => {
    if (root === exceptRoot) return;

    root.dataset.open = "false";
    root.querySelector("[data-contact-date-toggle]")?.setAttribute("aria-expanded", "false");
  });
};

const closeContactOverlays = (exceptRoot) => {
  closeContactSelects(exceptRoot);
  closeContactDatePickers(exceptRoot);
};

const contactDateLocale = () => document.documentElement.lang || "en";

const padDatePart = (value) => String(value).padStart(2, "0");

const toISODate = (year, month, day) => `${year}-${padDatePart(month + 1)}-${padDatePart(day)}`;

const parseISODate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");

  if (!match) return null;

  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const formatContactDateAria = (value) => {
  const date = parseISODate(value);

  if (!date) return value;

  return date.toLocaleDateString(contactDateLocale(), {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const escapeAttr = (value) => String(value).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

const formatContactDateLabel = (value) => {
  const date = parseISODate(value);

  if (!date) return "";

  return date.toLocaleDateString(contactDateLocale(), {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const syncContactSelectLabel = (root) => {
  const native = root.querySelector("[data-contact-select-native]");
  const label = root.querySelector("[data-contact-select-toggle-label]");
  const selected = native?.selectedOptions?.[0];
  const hasValue = Boolean(native?.value);

  root.classList.toggle("has-value", hasValue);

  if (label) {
    label.textContent = hasValue ? selected.textContent.trim() : label.dataset.placeholder || "";
  }

  root.querySelectorAll("[data-contact-select-option]").forEach((option) => {
    option.setAttribute("aria-selected", String(option.dataset.value === native?.value));
    option.tabIndex = -1;
  });
};

const contactOtherField = (root) =>
  root.closest("[data-contact-form]")?.querySelector("[data-contact-event-other]") ||
  root.parentElement?.querySelector("[data-contact-event-other]");

const resizeContactTextarea = (textarea) => {
  if (!textarea) return;

  textarea.style.height = "auto";
  textarea.style.height = `${textarea.scrollHeight}px`;
};

const syncContactSelectOther = (root, { focus = false } = {}) => {
  const native = root.querySelector("[data-contact-select-native]");
  const otherField = contactOtherField(root);
  const otherInput = otherField?.querySelector("[data-contact-event-other-input]");
  const otherValue = native?.dataset.contactSelectOtherValue;
  const isOther = Boolean(native && otherValue && native.value === otherValue);

  if (!otherField || !otherInput) return;

  otherField.dataset.open = isOther ? "true" : "false";

  if (isOther) {
    otherField.removeAttribute("inert");
    otherInput.required = true;
    otherInput.setAttribute("name", "contact[Event type]");
    native.removeAttribute("name");

    if (focus) otherInput.focus();
    requestAnimationFrame(() => resizeContactTextarea(otherInput));
    return;
  }

  otherField.setAttribute("inert", "");
  otherInput.required = false;
  otherInput.removeAttribute("name");
  native.setAttribute("name", "contact[Event type]");
};

const syncContactDateLabel = (root) => {
  const native = root.querySelector("[data-contact-date-input]");
  const label = root.querySelector("[data-contact-date-label]");
  const hasValue = Boolean(native?.value);

  root.classList.toggle("has-value", hasValue);

  if (label) {
    label.textContent = hasValue ? formatContactDateLabel(native.value) : label.dataset.placeholder || "";
  }
};

const weekdayLabels = () => {
  const formatter = new Intl.DateTimeFormat(contactDateLocale(), { weekday: "short" });

  return Array.from({ length: 7 }, (_, index) => formatter.format(new Date(2021, 7, 1 + index)).slice(0, 2));
};

const renderContactDatePicker = (root) => {
  const native = root.querySelector("[data-contact-date-input]");
  const monthLabel = root.querySelector("[data-contact-date-month]");
  const weekdays = root.querySelector("[data-contact-date-weekdays]");
  const grid = root.querySelector("[data-contact-date-grid]");
  const selected = parseISODate(native?.value);
  const view = parseISODate(`${root.dataset.viewMonth}-01`) || selected || new Date();
  const year = view.getFullYear();
  const month = view.getMonth();
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const previousMonthDays = new Date(year, month, 0).getDate();
  const today = toISODate(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
  const selectedValue = native?.value || "";
  const cells = [];

  root.dataset.viewMonth = `${year}-${padDatePart(month + 1)}`;

  if (monthLabel) {
    monthLabel.textContent = view.toLocaleDateString(contactDateLocale(), {
      month: "long",
      year: "numeric",
    });
  }

  if (weekdays && weekdays.childElementCount === 0) {
    weekdays.innerHTML = weekdayLabels()
      .map((label) => `<span>${label}</span>`)
      .join("");
  }

  for (let index = 0; index < firstWeekday; index += 1) {
    const day = previousMonthDays - firstWeekday + index + 1;
    const previous = month === 0 ? { year: year - 1, month: 11 } : { year, month: month - 1 };

    cells.push({
      day,
      iso: toISODate(previous.year, previous.month, day),
      outside: true,
    });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({
      day,
      iso: toISODate(year, month, day),
      outside: false,
    });
  }

  while (cells.length % 7 !== 0) {
    const day = cells.length - (firstWeekday + daysInMonth) + 1;
    const next = month === 11 ? { year: year + 1, month: 0 } : { year, month: month + 1 };

    cells.push({
      day,
      iso: toISODate(next.year, next.month, day),
      outside: true,
    });
  }

  if (grid) {
    grid.innerHTML = cells
      .map((cell) => {
        const classes = ["athoor-contact-page__calendar-day"];

        if (cell.outside) classes.push("is-outside");
        if (cell.iso === today) classes.push("is-today");
        if (cell.iso === selectedValue) classes.push("is-selected");

        const isTabStop = cell.iso === selectedValue || (!selectedValue && cell.iso === today);
        const ariaLabel = escapeAttr(formatContactDateAria(cell.iso));

        return `<button class="${classes.join(" ")}" type="button" tabindex="${isTabStop ? 0 : -1}" aria-label="${ariaLabel}" aria-pressed="${cell.iso === selectedValue}" data-contact-date-day="${cell.iso}">${cell.day}</button>`;
      })
      .join("");

    if (!grid.querySelector('[tabindex="0"]')) {
      const firstDay = grid.querySelector("[data-contact-date-day]:not(.is-outside)");
      if (firstDay) firstDay.tabIndex = 0;
    }
  }
};

const shiftContactDateMonth = (root, offset) => {
  const view = parseISODate(`${root.dataset.viewMonth}-01`) || new Date();
  const next = new Date(view.getFullYear(), view.getMonth() + offset, 1);

  root.dataset.viewMonth = `${next.getFullYear()}-${padDatePart(next.getMonth() + 1)}`;
  renderContactDatePicker(root);
  root.querySelector('[data-contact-date-day][tabindex="0"]')?.focus();
};

const hideNativeContactControl = (element) => {
  if (!element) return;

  element.setAttribute("tabindex", "-1");
  element.setAttribute("aria-hidden", "true");
};

const initContactFields = (scope = document) => {
  scope.querySelectorAll("[data-contact-select]").forEach((root) => {
    hideNativeContactControl(root.querySelector("[data-contact-select-native]"));
    syncContactSelectLabel(root);
    syncContactSelectOther(root);
  });
  scope.querySelectorAll("[data-contact-date]").forEach((root) => {
    hideNativeContactControl(root.querySelector("[data-contact-date-input]"));
    syncContactDateLabel(root);
  });
  scope.querySelectorAll("[data-contact-autogrow]").forEach((textarea) => {
    resizeContactTextarea(textarea);
  });
};

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const isVisibleFocusable = (element) => {
  if (element.closest("[hidden]")) return false;
  if (element.getAttribute("aria-hidden") === "true") return false;

  const style = window.getComputedStyle(element);

  return style.visibility !== "hidden" && style.display !== "none";
};

const trapFocus = (event, root) => {
  if (!root || event.key !== "Tab") return false;

  const items = [...root.querySelectorAll(FOCUSABLE_SELECTOR)].filter(isVisibleFocusable);

  if (!items.length) return false;

  const first = items[0];
  const last = items[items.length - 1];

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
    return true;
  }

  if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
    return true;
  }

  return false;
};

document.addEventListener("keydown", (event) => {
  const searchHeader = document.querySelector('[data-header][data-search-open="true"]');
  const menuHeader = document.querySelector('[data-header][data-menu-open="true"]');

  if (event.key === "Tab") {
    if (collectionFilterOpen) {
      const sidebar = getCollectionFilterForm()?.querySelector("[data-sticky-filter-sidebar]");
      trapFocus(event, sidebar);
      return;
    }

    if (searchHeader && trapFocus(event, searchHeader.querySelector("[data-header-search]"))) return;

    const menuToggle = menuHeader?.querySelector("[data-header-menu-toggle]");

    if (menuHeader && menuToggle && window.getComputedStyle(menuToggle).display !== "none") {
      trapFocus(event, menuHeader);
    }

    return;
  }

  const selectRoot = event.target.closest("[data-contact-select]");
  const dateRoot = event.target.closest("[data-contact-date]");

  if (selectRoot?.dataset.open === "true") {
    const options = [...selectRoot.querySelectorAll("[data-contact-select-option]")];
    const current = event.target.closest("[data-contact-select-option]");
    const currentIndex = options.indexOf(current);

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const delta = event.key === "ArrowDown" ? 1 : -1;
      const next = options[(Math.max(currentIndex, 0) + delta + options.length) % options.length];

      options.forEach((option) => {
        option.tabIndex = -1;
      });
      next.tabIndex = 0;
      next.focus();
      return;
    }

    if ((event.key === "Enter" || event.key === " ") && current) {
      event.preventDefault();
      current.click();
      return;
    }
  }

  if (
    selectRoot &&
    selectRoot.dataset.open !== "true" &&
    event.target.closest("[data-contact-select-toggle]") &&
    (event.key === "ArrowDown" || event.key === "ArrowUp")
  ) {
    event.preventDefault();
    selectRoot.querySelector("[data-contact-select-toggle]")?.click();
    return;
  }

  if (dateRoot?.dataset.open === "true") {
    const days = [...dateRoot.querySelectorAll("[data-contact-date-day]")];
    const current = event.target.closest("[data-contact-date-day]");
    const currentIndex = days.indexOf(current);

    if (current && ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) {
      event.preventDefault();
      const delta = event.key === "ArrowLeft" ? -1 : event.key === "ArrowRight" ? 1 : event.key === "ArrowUp" ? -7 : 7;
      const next = days[currentIndex + delta];

      if (!next) return;

      days.forEach((day) => {
        day.tabIndex = -1;
      });
      next.tabIndex = 0;
      next.focus();
      return;
    }
  }

  if (
    dateRoot &&
    dateRoot.dataset.open !== "true" &&
    event.target.closest("[data-contact-date-toggle]") &&
    event.key === "ArrowDown"
  ) {
    event.preventDefault();
    dateRoot.querySelector("[data-contact-date-toggle]")?.click();
    return;
  }

  if (event.key !== "Escape") return;

  if (collectionFilterOpen) {
    setCollectionFilterModalOpen(false, { restoreFocus: true });
    return;
  }

  document.querySelectorAll("[data-sort-menu][open]").forEach((menu) => {
    menu.removeAttribute("open");
  });

  const megaHeader = document.querySelector('[data-header][data-mega-open="true"]');

  if (megaHeader) {
    event.preventDefault();
    closeHeaderMega(megaHeader);
    megaHeader.querySelector("[data-header-mega-trigger] > a")?.focus({ preventScroll: true });
    return;
  }

  document.querySelectorAll('[data-header][data-menu-open="true"]').forEach((openHeader) => {
    closeHeaderMenu(openHeader);
    openHeader.querySelector("[data-header-menu-toggle]")?.focus();
  });

  document.querySelectorAll('[data-header][data-search-open="true"]').forEach((openHeader) => {
    closeHeaderSearch(openHeader, true);
  });

  const openSelect = event.target.closest("[data-contact-select][data-open='true']");
  const openDate = event.target.closest("[data-contact-date][data-open='true']");

  closeContactOverlays();
  openSelect?.querySelector("[data-contact-select-toggle]")?.focus();
  openDate?.querySelector("[data-contact-date-toggle]")?.focus();
});

document.addEventListener("click", (event) => {
  const selectRoot = event.target.closest("[data-contact-select]");
  const dateRoot = event.target.closest("[data-contact-date]");
  const option = event.target.closest("[data-contact-select-option]");
  const selectToggle = event.target.closest("[data-contact-select-toggle]");
  const dateToggle = event.target.closest("[data-contact-date-toggle]");
  const dateDay = event.target.closest("[data-contact-date-day]");
  const datePrev = event.target.closest("[data-contact-date-prev]");
  const dateNext = event.target.closest("[data-contact-date-next]");

  if (option && selectRoot) {
    const native = selectRoot.querySelector("[data-contact-select-native]");

    if (native) {
      native.value = option.dataset.value || "";
      native.dispatchEvent(new Event("change", { bubbles: true }));
    }

    syncContactSelectLabel(selectRoot);
    syncContactSelectOther(selectRoot, { focus: true });
    closeContactOverlays();

    const otherValue = native?.dataset.contactSelectOtherValue;

    if (native?.value !== otherValue) {
      selectRoot.querySelector("[data-contact-select-toggle]")?.focus();
    }

    return;
  }

  if (selectToggle && selectRoot) {
    const willOpen = selectRoot.dataset.open !== "true";

    closeContactOverlays(willOpen ? selectRoot : null);
    selectRoot.dataset.open = willOpen ? "true" : "false";
    selectToggle.setAttribute("aria-expanded", String(willOpen));

    if (willOpen) {
      const selected =
        [...selectRoot.querySelectorAll("[data-contact-select-option]")].find(
          (item) => item.getAttribute("aria-selected") === "true",
        ) || selectRoot.querySelector("[data-contact-select-option]");

      if (selected) {
        selected.tabIndex = 0;
        selected.focus();
      }
    }

    return;
  }

  if (dateDay && dateRoot) {
    const native = dateRoot.querySelector("[data-contact-date-input]");

    if (native) {
      native.value = dateDay.dataset.contactDateDay || "";
      native.dispatchEvent(new Event("change", { bubbles: true }));
    }

    syncContactDateLabel(dateRoot);
    closeContactOverlays();
    dateRoot.querySelector("[data-contact-date-toggle]")?.focus();
    return;
  }

  if ((datePrev || dateNext) && dateRoot) {
    shiftContactDateMonth(dateRoot, datePrev ? -1 : 1);
    return;
  }

  if (dateToggle && dateRoot) {
    const willOpen = dateRoot.dataset.open !== "true";

    closeContactOverlays(willOpen ? dateRoot : null);
    dateRoot.dataset.open = willOpen ? "true" : "false";
    dateToggle.setAttribute("aria-expanded", String(willOpen));

    if (willOpen) {
      renderContactDatePicker(dateRoot);
      dateRoot.querySelector('[data-contact-date-day][tabindex="0"]')?.focus();
    }

    return;
  }

  if (selectRoot || dateRoot) return;

  closeContactOverlays();
});

initContactFields();
document.addEventListener("shopify:section:load", (event) => initContactFields(event.target));

let partialNavigationScrollTimer = null;

window.addEventListener(
  "scroll",
  () => {
    if (!getCollectionSection() && !getEventSlider()) return;

    window.clearTimeout(partialNavigationScrollTimer);
    partialNavigationScrollTimer = window.setTimeout(savePartialNavigationScroll, 120);
  },
  { passive: true },
);

window.addEventListener("popstate", (event) => {
  const restoreScroll = event.state?.athoorScroll;

  if (getCollectionSection()) {
    renderCollectionSection(new URL(window.location.href), false, { restoreScroll });
    return;
  }

  if (getEventSlider()) {
    renderEventPage(new URL(window.location.href), false, { restoreScroll });
  }
});

document.addEventListener("shopify:section:load", (event) => initTestimonialAutoplay(event.target));

// Move collection photos gently behind their clipped frames at every screen size.
(() => {
  const preference = window.matchMedia("(prefers-reduced-motion: no-preference)");
  const frames = new Set();
  const visible = new Set();
  let pendingFrame = 0;

  const render = () => {
    pendingFrame = 0;
    if (!preference.matches) return;
    const viewportHeight = window.innerHeight;
    const positions = [...visible]
      .filter((frame) => frame.isConnected)
      .map((frame) => {
        const bounds = frame.getBoundingClientRect();
        const progress = Math.max(0, Math.min(1, (viewportHeight - bounds.top) / (viewportHeight + bounds.height)));
        const travel = window.innerWidth <= 1024 ? 24 : 48;
        return [frame, (progress - 0.5) * travel];
      });
    positions.forEach(([frame, offset]) => {
      frame.style.setProperty("--collection-parallax-y", `${offset.toFixed(2)}px`);
    });
  };
  const schedule = () => {
    if (preference.matches && visible.size && !pendingFrame) {
      pendingFrame = window.requestAnimationFrame(render);
    }
  };
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting) visible.add(target);
        else visible.delete(target);
      });
      schedule();
    },
    { rootMargin: "80px 0px" },
  );

  const init = (scope = document) => {
    getScopedElements(scope, ".athoor-collection-overview__image").forEach((frame) => {
      if (frames.has(frame) || !frame.querySelector("img")) return;
      frames.add(frame);
      frame.classList.toggle("is-parallax", preference.matches);
      observer.observe(frame);
    });
    schedule();
  };
  preference.addEventListener("change", () => {
    frames.forEach((frame) => {
      frame.classList.toggle("is-parallax", preference.matches);
      frame.style.removeProperty("--collection-parallax-y");
    });
    schedule();
  });
  window.addEventListener("scroll", schedule, { passive: true });
  window.addEventListener("resize", schedule, { passive: true });
  document.addEventListener("shopify:section:load", (event) => init(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    frames.forEach((frame) => {
      if (!event.target.contains(frame)) return;
      observer.unobserve(frame);
      visible.delete(frame);
      frames.delete(frame);
    });
  });
  init();
})();

// Size the full-width bottom fade from the paragraph's untransformed position.
const initHomeTextShade = (scope = document) => {
  getScopedElements(scope, ".athoor-home-banner").forEach((banner) => {
    if (banner.dataset.textShadeReady) return;
    const paragraph = banner.querySelector(".athoor-home-banner__text");
    if (!paragraph) return;
    banner.dataset.textShadeReady = "true";
    const update = () => {
      let top = 0;
      let element = paragraph;
      while (element && element !== banner) {
        top += element.offsetTop;
        element = element.offsetParent;
      }
      banner.style.setProperty("--home-text-shade-height", `${Math.max(0, banner.clientHeight - top)}px`);
    };
    const observer = new ResizeObserver(update);
    observer.observe(banner);
    observer.observe(paragraph);
    const cleanup = (event) => {
      if (!event.target.contains(banner)) return;
      observer.disconnect();
      document.removeEventListener("shopify:section:unload", cleanup);
    };
    document.addEventListener("shopify:section:unload", cleanup);
    update();
  });
};
runAfterHeroPaint(initHomeTextShade);
document.addEventListener("shopify:section:load", (event) => initHomeTextShade(event.target));
